import useCampaignResultsAvailability from './useCampaignResultsAvailability';
import { campaignResultsColumn } from './campaignResultsColumn';
import OnboardingProgress from './OnboardingProgress';
import useOnboardingProgress from './useOnboardingProgress';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Button, Input, Select, Space, Typography, Dropdown, DatePicker, App, notification, Row, Tabs, Tooltip, Popover, Modal, Progress, Spin, Collapse, Card, Badge, Empty } from "antd";
import { PlusOutlined, SearchOutlined, DownloadOutlined, FilterOutlined, InfoCircleOutlined, QuestionCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined, WarningOutlined, DownSquareOutlined, ExportOutlined, EditOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import nodeAPI from "../../../service/ApiServiceNode";
import ExportProgressModal from "../../Finance/Common/ExportProgressModal";
import EditableCell from "../../Tasks/UnifiedTaskView/EditableCell";
import { createAuthorizedEventSource } from "../../../utils/sseClient";
import roleService, { ROLES } from "../../../service/RoleService";
// EstimatedBillingsModal removed - using tooltip instead
import { Modal as DSModal } from "../../../design-system";
import { BUTTON_PRESETS } from "../../../design-system/constants";
import { ISSUE_DOCUMENTATION } from "./IssueDocumentation";
import { buildIssueScannerQueryParams } from "./issueScannerQueryParams";
import { isProjectVendorPlanningIncomplete } from "./projectFinanceStatus";
import { getProjectTableRowKey } from "./projectTableRowKey";
import {
    applyProjectListSortParams,
    getProjectListSortFromTableSorter,
    getProjectListSortOrderForColumn,
    PROJECT_LIST_SORT_FIELDS,
} from "./projectTableSort";
import "../project-management.scss";
import "../modal-shared.scss";
import { getApiErrorMessage } from '../../../utils/errorUtils';
import logger from '../../../utils/logger';



const { Option } = Select;
const { RangePicker } = DatePicker;

export const PROJECT_CURRENCY_MODES = {
    SGD: "SGD",
    USD: "USD",
    LOCAL: "LOCAL",
};

const CATEGORY_GROUPS = {
    VENDOR_BILL_ISSUES: {
        label: "Vendor bill issues",
        description: "Vendor planning, vendor revenue, and vendor bill issues",
        categories: [
            'VENDOR_PLANNING_EMPTY',
            'VENDOR_REVENUE_PLANNED_COST_DISCREPANCY',
            'VENDOR_BILLS_OVER_REVENUE',
            'REVENUE_NOT_ALLOCATED'
        ]
    },
    CLIENT_INVOICE_ISSUES: {
        label: "Client invoice issues",
        description: "Sales order and client invoice issues",
        categories: [
            'NO_SALES_ORDERS',
            'NO_CLIENT_INVOICES',
        ]
    },
    REVENUE_RECOGNITION_ISSUES: {
        label: "Revenue recognition issues",
        description: "Project timing and archive readiness issues",
        categories: [
            'PROJECTS_PAST_END_DATE',
            'DATE_PRE_START_HOURS',
            'PROJECTS_TO_ARCHIVE'
        ]
    }
};

const ISSUE_REMARK_CATEGORIES = new Set([
    ...Object.values(CATEGORY_GROUPS).flatMap((group) => group.categories),
]);

const getIssueRemarkKey = (projectId, issueCategory) => `${projectId}:${issueCategory}`;

const buildRemarkMap = (remarks = []) => {
    const map = new Map();
    remarks.forEach((remark) => {
        if (remark?.projectId && remark?.issueCategory) {
            map.set(getIssueRemarkKey(remark.projectId, remark.issueCategory), remark);
        }
    });
    return map;
};

const mergeIssueRemarksIntoGroups = (issueGroups = [], remarks = []) => {
    const remarkMap = buildRemarkMap(remarks);

    return issueGroups.map((group) => {
        if (!ISSUE_REMARK_CATEGORIES.has(group.category)) {
            return group;
        }

        const mergeProject = (project) => ({
            ...project,
            issueRemark: remarkMap.get(getIssueRemarkKey(project.project_id, group.category)) || null
        });

        return {
            ...group,
            projects: (group.projects || []).map(mergeProject),
            groupedByOwner: Array.isArray(group.groupedByOwner)
                ? group.groupedByOwner.map((ownerGroup) => ({
                    ...ownerGroup,
                    projects: (ownerGroup.projects || []).map(mergeProject)
                }))
                : group.groupedByOwner
        };
    });
};

const mergeSingleIssueRemarkIntoGroups = (issueGroups = [], savedRemark) => {
    if (!savedRemark) {
        return issueGroups;
    }

    return mergeIssueRemarksIntoGroups(issueGroups, [savedRemark]).map((mergedGroup, index) => {
        const originalGroup = issueGroups[index];
        if (mergedGroup.category !== savedRemark.issueCategory) {
            return originalGroup;
        }

        return {
            ...originalGroup,
            projects: (originalGroup.projects || []).map((project) => (
                project.project_id === savedRemark.projectId
                    ? { ...project, issueRemark: savedRemark }
                    : project
            )),
            groupedByOwner: Array.isArray(originalGroup.groupedByOwner)
                ? originalGroup.groupedByOwner.map((ownerGroup) => ({
                    ...ownerGroup,
                    projects: (ownerGroup.projects || []).map((project) => (
                        project.project_id === savedRemark.projectId
                            ? { ...project, issueRemark: savedRemark }
                            : project
                    ))
                }))
                : originalGroup.groupedByOwner
        };
    });
};

const renderRemarkTextWithLinks = (text) => {
    const value = String(text || '');
    const parts = value.split(/(https?:\/\/[^\s]+)/g);

    return parts.map((part, index) => {
        if (/^https?:\/\//.test(part)) {
            return (
                <a
                    key={`${part}-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1b1b1b', textDecoration: 'underline' }}
                >
                    {part}
                </a>
            );
        }

        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
};

const formatIssueRemarkTimestamp = (value) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const IssueRemarkModal = ({
    visible,
    editor,
    saving,
    onClose,
    onSave,
}) => {
    const [draft, setDraft] = useState("");

    useEffect(() => {
        if (visible) {
            setDraft(editor.remark || "");
        }
    }, [editor.remark, visible]);

    return (
        <DSModal
            visible={visible}
            onClose={onClose}
            width="medium"
        >
            <DSModal.Header
                title="Issue Remark"
                subtitle={editor.projectLabel}
            />
            <DSModal.Body style={{ padding: '24px 0' }}>
                <Input.TextArea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    maxLength={2000}
                    rows={8}
                    showCount
                    placeholder="Add finance remark"
                    disabled={saving}
                />
            </DSModal.Body>
            <DSModal.Footer
                buttons={[
                    {
                        ...BUTTON_PRESETS.CANCEL,
                        key: 'cancel',
                        label: 'Cancel',
                        onClick: onClose,
                        disabled: saving
                    },
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: 'save',
                        label: 'Save',
                        onClick: () => onSave(draft),
                        loading: saving
                    }
                ]}
            />
        </DSModal>
    );
};

const ProjectsTable = ({
    // External data props (backward compatibility)
    projects: externalProjects = null,
    loading: externalLoading = null,
    pagination: externalPagination = null,

    // Context props (defines what projects to show)
    clientId = null,
    clientContactId = null,
    picId = null,
    vendorId = null,
    myProjects = false,
    assignedUser = null,
    assignedUserEmail = null,

    // UI configuration props
    title = "PROJECTS",
    showAddButton = true,
    onAddProject = null,
    isMyProjects = false,

    // Tab control
    activeTab = "confirmed",
    onTabChange = null,
    showProspectTab = true,

    // External filter control (optional - for backward compatibility)
    retainerFilter: externalRetainerFilter,
    onRetainerChange: externalOnRetainerChange,
    searchText: externalSearchText,
    onSearch: externalOnSearch,
    dateRange: externalDateRange,
    onDateRangeChange: externalOnDateRangeChange,
    departmentFilter: externalDepartmentFilter,
    onDepartmentChange: externalOnDepartmentChange,
    projectTypeFilter: externalProjectTypeFilter,
    onProjectTypeChange: externalOnProjectTypeChange,
    industryFilter: externalIndustryFilter,
    onIndustryChange: externalOnIndustryChange,
    statusFilter: externalStatusFilter,
    onStatusChange: externalOnStatusChange,
    onPageChange: externalOnPageChange,
    sortState: externalSortState,
    onSortChange: externalOnSortChange,
    onRefresh: externalOnRefresh,
    onEnhancedExport = null,
    onExport = null,
    onVendorBreakdownExport = null,
    creditLimitAmountSgd = null,
    currencyMode: externalCurrencyMode,
    onCurrencyModeChange: externalOnCurrencyModeChange,
}) => {
    // Get message from App context
    const { message } = App.useApp();

    // Internal data states for self-managed mode
    const [internalProjects, setInternalProjects] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalPagination, setInternalPagination] = useState({
        page: 1,
        limit: 20,
        total: 0
    });

    // Use external or internal data
    const projects = externalProjects !== null ? externalProjects : internalProjects;
    const onboarding = useOnboardingProgress([], ["confirmed", "lead"].includes(activeTab) ? projects.map(project => project.project_id || project.id) : []);
    const campaignAvailability = useCampaignResultsAvailability(projects);
    const loading = externalLoading !== null ? externalLoading : internalLoading;
    const pagination = externalPagination !== null ? externalPagination : internalPagination;
    // API data states
    const [departments, setDepartments] = useState([]);
    const [projectClassifications, setProjectClassifications] = useState([]);
    const [industryClassifications, setIndustryClassifications] = useState([]);

    // Currency toggle state
    const [localCurrencyMode, setLocalCurrencyMode] = useState(PROJECT_CURRENCY_MODES.SGD);
    const currencyMode = externalCurrencyMode || localCurrencyMode;
    const setCurrencyMode = externalOnCurrencyModeChange || setLocalCurrencyMode;
    const [expandedCreditRowKeys, setExpandedCreditRowKeys] = useState([]);
    const fetchProjectsRequestSeqRef = useRef(0);

    // Filter states
    const [localSearchText, setLocalSearchText] = useState("");
    const searchText =
        externalSearchText !== undefined ? externalSearchText : localSearchText;
    const setSearchText = externalOnSearch || setLocalSearchText;

    const [localDateRange, setLocalDateRange] = useState([null, null]);
    const dateRange =
        externalDateRange !== undefined ? externalDateRange : localDateRange;
    const setDateRange = externalOnDateRangeChange || setLocalDateRange;

    const [localDepartmentFilter, setLocalDepartmentFilter] = useState([]);
    const departmentFilter = externalDepartmentFilter ?? localDepartmentFilter;
    const setDepartmentFilter =
        externalOnDepartmentChange || setLocalDepartmentFilter;

    const [localStatusFilter, setLocalStatusFilter] = useState([]);
    const statusFilter = externalStatusFilter ?? localStatusFilter;
    const setStatusFilter = externalOnStatusChange || setLocalStatusFilter;

    const [localProjectTypeFilter, setLocalProjectTypeFilter] = useState([]);
    const projectTypeFilter = externalProjectTypeFilter ?? localProjectTypeFilter;
    const setProjectTypeFilter =
        externalOnProjectTypeChange || setLocalProjectTypeFilter;

    const [localIndustryFilter, setLocalIndustryFilter] = useState([]);
    const industryFilter = externalIndustryFilter ?? localIndustryFilter;
    const setIndustryFilter =
        externalOnIndustryChange || setLocalIndustryFilter;

    // Use external retainer filter if provided, otherwise manage locally
    const [localRetainerFilter, setLocalRetainerFilter] = useState("all");
    const retainerFilter =
        externalRetainerFilter !== undefined
            ? externalRetainerFilter
            : localRetainerFilter;
    const setRetainerFilter =
        externalOnRetainerChange || setLocalRetainerFilter;

    const [localSortState, setLocalSortState] = useState({
        orderBy: null,
        order: null,
    });
    const sortState = externalSortState ?? localSortState;
    const setSortState = externalOnSortChange || setLocalSortState;

    // Export modal state
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportLocation] = useState("local");
    const [exportLoading, setExportLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [exportError, setExportError] = useState(null);

    const [exportJob, setExportJob] = useState(null);

    // Monthly export modal state
    const [monthlyExportModalVisible, setMonthlyExportModalVisible] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(undefined);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [monthlyExportLoading, setMonthlyExportLoading] = useState(false);
    const [monthlyExportProgress, setMonthlyExportProgress] = useState(0);
    const [monthlyExportError, setMonthlyExportError] = useState(null);
    // Duplicate modal states
    const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
    const [projectToDuplicate, setProjectToDuplicate] = useState(null);
    const [duplicateLoading, setDuplicateLoading] = useState(false);

    // Delete modal states
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Issues tab state
    const [issuesData, setIssuesData] = useState(null);
    const [issuesLoading] = useState(false);

    // Issues scanner state (new optimized approach)
    useState([]);
    const [, setScanProgress] = useState({});
    const [isScanning, setIsScanning] = useState(false);
    const [hasScanned, setHasScanned] = useState(false); // Track if scan has been performed
    const [progress, setProgress] = useState({ current: 0, total: 0 }); // Real-time progress from SSE
    const [issueRemarkEditor, setIssueRemarkEditor] = useState({
        visible: false,
        projectId: null,
        issueCategory: null,
        projectLabel: "",
        remark: ""
    });
    const [issueRemarkSaving, setIssueRemarkSaving] = useState(false);

    // Refs for SSE management
    const eventSourceRef = useRef(null);
    const isLoadingRef = useRef(false);
    const closingEventSourcesRef = useRef(new WeakSet());
    const terminalEventSourcesRef = useRef(new WeakSet());

    // Date filter state for issues scanner
    const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'thisMonth', 'lastMonth', 'last3Months', 'thisFY', 'lastFY', 'custom'
    const [customDateRange, setCustomDateRange] = useState([null, null]); // [startDate, endDate]

    // Active issues tab state
    const [activeIssuesTab, setActiveIssuesTab] = useState('VENDOR_BILL_ISSUES');

    // Individual category definitions
    const ISSUE_CATEGORIES = {
        VENDOR_BILLS_OVER_REVENUE: {
            label: 'Vendor bills over Vendor revenue',
            critical: true,
            description: 'Actual vendor bills exceed vendor revenue (losing money)'
        },
        VENDOR_BILLS_OVER_PLANNED: {
            label: 'Vendor Bills Over Planned',
            critical: true,
            description: 'Actual vendor bills exceed planned cost (over budget)'
        },
        PROJECTS_PAST_END_DATE: {
            label: 'Project past end date',
            critical: true,
            description: 'Projects past their end date but not finished'
        },
        REVENUE_NOT_ALLOCATED: {
            label: 'Revenue not allocated',
            critical: true,
            description: 'Projects need revenue re-allocation (vendor revenue = $0)'
        },
        VENDOR_REVENUE_PLANNED_COST_DISCREPANCY: {
            label: 'Discrepancy between Vendor revenue and vendor planned cost',
            critical: false,
            description: 'Vendor revenue does not match vendor planned cost'
        },
        VENDOR_REVENUE_LOW: {
            label: 'Vendor Revenue Low',
            critical: false,
            description: 'Vendor revenue lower than planned cost'
        },
        VENDOR_REVENUE_HIGH: {
            label: 'Vendor Revenue High',
            critical: false,
            description: 'Vendor revenue higher than planned cost'
        },
        INVOICE_ALLOCATION_VARIANCE: {
            label: 'Invoice Allocation Variance',
            critical: false,
            description: 'Invoices with allocation variance'
        },
        VENDOR_PLANNING_EMPTY: {
            label: 'Vendor planning empty',
            critical: false,
            description: 'Estimated vendor cost without vendor planning'
        },
        DATE_PRE_START_HOURS: {
            label: 'Pre start hours logged',
            critical: false,
            description: 'In-Planning or future projects with hours logged (≥10h)'
        },
        PROJECTS_TO_ARCHIVE: {
            label: 'Projects to Archive',
            critical: false,
            description: 'Finished projects that should be archived'
        },
        VENDOR_LOSS_FINISHED: {
            label: 'Vendor Loss (Finished/Archived)',
            critical: false,
            description: 'Finished/Archived projects with vendor loss'
        },
        UNPROFITABLE_FINISHED: {
            label: 'Unprofitable Projects',
            critical: false,
            description: 'Unprofitable finished/archived projects'
        },
        HIGHLY_PROFITABLE_ARCHIVED: {
            label: 'Highly Profitable Archived',
            critical: false,
            description: 'Materially profitable archived projects (margin ≥10%)'
        },
        NO_CLIENT_INVOICES: {
            label: 'No Client invoice',
            critical: false,
            description: 'Confirmed projects without client invoices'
        },
        NO_SALES_ORDERS: {
            label: 'No SO',
            critical: false,
            description: 'Confirmed projects without sales orders'
        },
        INVOICES_AFTER_END_DATE: {
            label: 'Invoices Outside Date Range',
            critical: false,
            description: 'Projects with invoices outside project start/end dates'
        },
        BILLS_AFTER_END_DATE: {
            label: 'Bills Outside Date Range',
            critical: false,
            description: 'Projects with vendor bills outside project start/end dates'
        },
        TIME_OUTSIDE_DATE_RANGE: {
            label: 'Time Entries Outside Date Range',
            critical: false,
            description: 'Projects with time entries outside project start/end dates'
        },
        WORK_ACCOMPLISHED_OUTSIDE_RANGE: {
            label: 'Work Accomplished Date Outside Range',
            critical: false,
            description: 'Projects with invoice work accomplished dates outside project start/end dates'
        }
    };

    // Fetch API data on component mount
    useEffect(() => {
        // Fetch departments from API
        const fetchDepartments = async () => {
            try {
                const response = await nodeAPI.get(
                    "/company-directory/department"
                );
                const data = response.data.data || response.data;
                // Sort departments alphabetically and filter out null ids
                const sortedDepartments = data
                    .filter(d => d.id != null) // Filter out null/undefined ids
                    .sort((a, b) => a.name.localeCompare(b.name));
                setDepartments(sortedDepartments);
            } catch (error) {
                logger.error("Error fetching departments:", error);
                // Fallback to hardcoded departments if API fails
                setDepartments([
                    { id: 1, name: "ACCOUNT MANAGEMENT" },
                    { id: 2, name: "ADMIN" },
                    { id: 3, name: "CREATIVE" },
                    { id: 4, name: "DATA & ANALYTICS" },
                    { id: 5, name: "FINANCE" },
                    { id: 6, name: "HUMAN RESOURCES AND ADMIN" },
                    { id: 7, name: "INSIGHTS AND STRATEGY" },
                    { id: 8, name: "MANAGEMENT" },
                    { id: 9, name: "MARCOM" },
                    { id: 10, name: "MEDIA" },
                    { id: 11, name: "PROJECT MANAGEMENT" },
                    { id: 12, name: "SEO" },
                    { id: 13, name: "SOCIAL & CONTENT" },
                    { id: 14, name: "TECHNOLOGY" },
                    { id: 15, name: "UXUI" },
                ]);
            }
        };

        // Fetch project classifications from API
        const fetchProjectClassifications = async () => {
            try {
                const response = await nodeAPI.get(
                    "/project/classifications?view=dropdown"
                );
                // Handle both response patterns from API
                const classifications =
                    response.data.data.data ||
                    response.data.data ||
                    response.data;
                // Sort project classifications alphabetically and filter out null ids
                const sortedClassifications = classifications
                    .filter(c => c.id != null) // Filter out null/undefined ids
                    .sort((a, b) => a.name.localeCompare(b.name));
                setProjectClassifications(sortedClassifications);
            } catch (error) {
                logger.error("Error fetching project classifications:", error);
                // Fallback to hardcoded project types if API fails
                setProjectClassifications([
                    { id: 1, name: "AI" },
                    { id: 2, name: "API" },
                    { id: 3, name: "ABOVE THE LINE MEDIA BUY" },
                    { id: 4, name: "ADVERTORIAL BUY" },
                    { id: 5, name: "AFFILIATE" },
                    { id: 6, name: "ANALYTICS AUDIT" },
                    { id: 7, name: "ANALYTICS IMPLEMENTATION" },
                    { id: 8, name: "APP DASHBOARD" },
                    { id: 9, name: "APP DESIGN" },
                    { id: 10, name: "ARTICLE" },
                    { id: 11, name: "BANNER HTML" },
                    { id: 12, name: "BANNER STATIC" },
                    { id: 13, name: "BROCHURE" },
                    { id: 14, name: "CAMPAIGN KV" },
                ]);
            }
        };

        // Fetch industry classifications from API
        const fetchIndustryClassifications = async () => {
            try {
                const response = await nodeAPI.get(
                    "/project/clients/industry-classifications"
                );
                // Handle response structure
                const industries =
                    response.data.data.industries ||
                    response.data.industries ||
                    response.data;
                // Sort industry classifications alphabetically and filter out null ids
                const sortedIndustries = industries
                    .filter(i => i.id != null) // Filter out null/undefined ids
                    .sort((a, b) => a.name.localeCompare(b.name));
                setIndustryClassifications(sortedIndustries);
            } catch (error) {
                logger.error(
                    "Error fetching industry classifications:",
                    error
                );
                setIndustryClassifications([]);
            }
        };

        fetchDepartments();
        fetchProjectClassifications();
        fetchIndustryClassifications();
    }, []);

    const fetchProjects = useCallback(async () => {
        const requestSeq = fetchProjectsRequestSeqRef.current + 1;
        fetchProjectsRequestSeqRef.current = requestSeq;

        try {
            setInternalLoading(true);

            const params = {
                page: internalPagination.page,
                limit: internalPagination.limit,
            };
            applyProjectListSortParams(params, sortState);

            // Add type filter based on tab
            if (activeTab && activeTab !== "all") {
                params.type = activeTab;
            }

            // Add context filters
            if (clientId) params.client_id = clientId;
            if (clientContactId) params.clientPic = clientContactId;
            if (picId) params.pic_id = picId;
            if (vendorId) params.vendor = vendorId;
            if (assignedUser) params.assigned_user = assignedUser;
            // myProjects: backend auto-detects from auth token

            // Add supplementary filters
            if (searchText && searchText.trim()) {
                params.search = searchText.trim();
            }

            if (dateRange && dateRange[0]) {
                params.startDate = dateRange[0].format("YYYY-MM-DD");
            }
            if (dateRange && dateRange[1]) {
                params.endDate = dateRange[1].format("YYYY-MM-DD");
            }

            if (departmentFilter && departmentFilter.length > 0) {
                params.department_ids = departmentFilter.join(",");
            }

            if (projectTypeFilter && projectTypeFilter.length > 0) {
                params.classification_ids = projectTypeFilter.join(",");
            }

            if (industryFilter && industryFilter.length > 0) {
                params.industry_ids = industryFilter.join(",");
            }

            if (retainerFilter === "retainer") {
                params.retainer = "true";
            } else if (retainerFilter === "non-retainer") {
                params.retainer = "false";
            }

            // Add status/lead filter based on active tab
            if (statusFilter && statusFilter.length > 0) {
                if (activeTab === "lead") {
                    const hasIssueFilter = statusFilter.includes('DateIssue') || statusFilter.includes('FinanceIssue');
                    if (hasIssueFilter) {
                        params.lead_list = statusFilter.join(",");
                    } else {
                        params.lead_list = statusFilter.join(",");
                    }
                } else {
                    params.status_list = statusFilter.join(",");
                }
            }

            // Choose endpoint based on context
            const endpoint = myProjects || isMyProjects ? '/project/my-projects' : '/project';

            const response = await nodeAPI.get(endpoint, { params });
            if (requestSeq !== fetchProjectsRequestSeqRef.current) {
                return;
            }

            // Handle response
            const projectsData = response.data.data?.projects || response.data.projects || [];
            setInternalProjects(projectsData);

            // Handle pagination
            const paginationData = response.data.pagination || response.data.data?.pagination;
            if (paginationData) {
                setInternalPagination(prev => ({
                    ...prev,
                    page: paginationData.page || prev.page,
                    limit: paginationData.limit || prev.limit,
                    total: paginationData.total || 0
                }));
            }
        } catch (error) {
            if (requestSeq !== fetchProjectsRequestSeqRef.current) {
                return;
            }

            logger.error("Error fetching projects:", error);
            message.error("Failed to load projects");
        } finally {
            if (requestSeq === fetchProjectsRequestSeqRef.current) {
                setInternalLoading(false);
            }
        }
    }, [
        activeTab,
        assignedUser,
        clientContactId,
        clientId,
        dateRange,
        departmentFilter,
        industryFilter,
        internalPagination.limit,
        internalPagination.page,
        isMyProjects,
        message,
        myProjects,
        picId,
        projectTypeFilter,
        retainerFilter,
        searchText,
        sortState,
        statusFilter,
        vendorId,
    ]);

    // Fetch projects data when in self-managed mode
    useEffect(() => {
        if (externalProjects === null) {
            fetchProjects();
        }
    }, [externalProjects, fetchProjects]);

    // Fetch issues data for the Issues tab
    

    // REMOVED: Auto-fetch issues - now replaced with manual scan button
    // This prevents memory crash by allowing users to select which issues to scan

    // Helper function to calculate date ranges
    const getDateRange = useCallback(() => {
        const now = new Date();
        let startDate = null;
        let endDate = null;
        const formatLocalDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        switch (dateFilterType) {
            case 'thisMonth':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'last3Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'thisFY': {
                // Financial year runs from Nov 1 to Oct 31
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth(); // 0-11
                if (currentMonth >= 10) { // Nov or Dec (10 or 11)
                    startDate = new Date(currentYear, 10, 1); // Nov 1 this year
                    endDate = new Date(currentYear + 1, 9, 31); // Oct 31 next year
                } else { // Jan to Oct (0-9)
                    startDate = new Date(currentYear - 1, 10, 1); // Nov 1 last year
                    endDate = new Date(currentYear, 9, 31); // Oct 31 this year
                }
                break;
            }
            case 'lastFY': {
                // Last financial year
                const currentYearLastFY = now.getFullYear();
                const currentMonthLastFY = now.getMonth();
                if (currentMonthLastFY >= 10) { // Nov or Dec
                    startDate = new Date(currentYearLastFY - 1, 10, 1); // Nov 1 last year
                    endDate = new Date(currentYearLastFY, 9, 31); // Oct 31 this year
                } else { // Jan to Oct
                    startDate = new Date(currentYearLastFY - 2, 10, 1); // Nov 1 two years ago
                    endDate = new Date(currentYearLastFY - 1, 9, 31); // Oct 31 last year
                }
                break;
            }
            case 'custom':
                if (customDateRange[0] && customDateRange[1]) {
                    startDate = customDateRange[0].toDate();
                    endDate = customDateRange[1].toDate();
                }
                break;
            case 'all':
            default:
                // No date filter
                return null;
        }

        if (startDate && endDate) {
            return {
                startDate: formatLocalDate(startDate),
                endDate: formatLocalDate(endDate)
            };
        }

        return null;
    }, [customDateRange, dateFilterType]);

    // Helper function to format issue-tab currency: symbol collated to amount (S$1,000, RM225,000)
    const formatIssueCurrency = (amount, record) => {
        const symbol = record.currencySymbol || record.currency_symbol || record.currency || '$';
        const formattedAmount = (amount || 0).toLocaleString();
        return `${symbol}${formattedAmount}`;
    };

    const formatSgdCurrency = (amount) => {
        const formattedAmount = (amount || 0).toLocaleString();
        return `S$${formattedAmount}`;
    };

    // Helper function to format issue-tab totals: symbol collated to amount
    const formatTotalCurrency = (amount, totals) => {
        const formattedAmount = (amount || 0).toLocaleString();
        if (totals?.currency === 'mixed') {
            return `$${formattedAmount}`;
        }
        const symbol = totals?.currencySymbol || totals?.currency_symbol || totals?.currency || '$';
        return `${symbol}${formattedAmount}`;
    };

    const isIssuePicDeactivated = (record) => {
        const activeFlag = record?.pic_is_active ?? record?.picIsActive ?? record?.pic_active;

        if (activeFlag === false || activeFlag === 0 || activeFlag === '0') {
            return true;
        }

        const status = String(record?.pic_status || record?.picStatus || '').toLowerCase();
        return status === 'deactivated' || status === 'inactive';
    };

    const renderIssuePicName = (picName, record) => {
        const displayName = picName || '-';

        if (!isIssuePicDeactivated(record)) {
            return displayName;
        }

        return (
            <Tooltip title="This PIC is deactivated">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <WarningOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                    <span style={{ color: 'rgba(0, 0, 0, 0.65)', textDecoration: 'line-through' }}>
                        {displayName}
                    </span>
                    <span
                        style={{
                            color: '#ff4d4f',
                            background: '#fff1f0',
                            border: '1px solid #ffccc7',
                            borderRadius: 4,
                            padding: '0 4px',
                            fontSize: 11,
                            fontWeight: 500,
                            lineHeight: '16px'
                        }}
                    >
                        Deactivated
                    </span>
                </span>
            </Tooltip>
        );
    };

    // Helper function to render issue documentation popover content
    const getIssueDocumentationPopover = (category) => {
        const doc = ISSUE_DOCUMENTATION[category];
        if (!doc) return null;

        return (
            <div style={{ maxWidth: 450 }}>
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
                    {doc.title}
                </Typography.Title>
                <Typography.Paragraph style={{ marginBottom: 12 }}>
                    {doc.description}
                </Typography.Paragraph>
                {doc.formula && (
                    <div style={{ marginBottom: 12 }}>
                        <Typography.Text strong>Detection Formula:</Typography.Text>
                        <Typography.Paragraph style={{
                            fontFamily: 'Consolas, Monaco, monospace',
                            backgroundColor: '#f5f5f5',
                            padding: '8px 12px',
                            marginTop: 4,
                            borderRadius: 4,
                            fontSize: 13
                        }}>
                            {doc.formula}
                        </Typography.Paragraph>
                    </div>
                )}
                {doc.examples && doc.examples.length > 0 && (
                    <div>
                        <Typography.Text strong>Examples:</Typography.Text>
                        <ul style={{ marginTop: 4, paddingLeft: 20, marginBottom: 0 }}>
                            {doc.examples.map((example, idx) => (
                                <li key={idx} style={{ marginBottom: 8 }}>
                                    <Typography.Text>{example}</Typography.Text>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // New SSE scan function - real-time progress updates

    const canEditIssueRemarks = useCallback(() => {
        return true;
    }, []);

    const fetchIssueRemarksForGroups = useCallback(async (issueGroups, categoriesToScan) => {
        if (!categoriesToScan.some((category) => ISSUE_REMARK_CATEGORIES.has(category))) {
            return issueGroups;
        }

        const projectIds = [...new Set(
            issueGroups.flatMap((group) => (group.projects || []).map((project) => project.project_id)).filter(Boolean)
        )];

        if (projectIds.length === 0) {
            return issueGroups;
        }

        const response = await nodeAPI.get('/project/issues/remarks', {
            params: {
                categories: categoriesToScan.filter((category) => ISSUE_REMARK_CATEGORIES.has(category)).join(','),
                projectIds: projectIds.join(',')
            }
        });
        const remarks = response.data?.data?.remarks || response.data?.remarks || [];

        return mergeIssueRemarksIntoGroups(issueGroups, remarks);
    }, []);

    const openIssueRemarkEditor = useCallback((record, issueCategory) => {
        const remark = record.issueRemark?.remark || "";
        setIssueRemarkEditor({
            visible: true,
            projectId: record.project_id,
            issueCategory,
            projectLabel: `${record.generated_id || record.project_id} - ${record.name || ''}`.trim(),
            remark
        });
    }, []);

    const closeIssueRemarkEditor = useCallback(() => {
        if (issueRemarkSaving) {
            return;
        }
        setIssueRemarkEditor({
            visible: false,
            projectId: null,
            issueCategory: null,
            projectLabel: "",
            remark: ""
        });
    }, [issueRemarkSaving]);

    const saveIssueRemark = useCallback(async (draft) => {
        if (!issueRemarkEditor.projectId || !issueRemarkEditor.issueCategory) {
            return;
        }

        try {
            setIssueRemarkSaving(true);
            const response = await nodeAPI.put('/project/issues/remarks', {
                projectId: issueRemarkEditor.projectId,
                issueCategory: issueRemarkEditor.issueCategory,
                remark: draft
            });
            const savedRemark = response.data?.data?.remark || response.data?.remark;

            setIssuesData((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    issueGroups: mergeSingleIssueRemarkIntoGroups(prev.issueGroups, savedRemark)
                };
            });

            message.success('Remark saved');
            setIssueRemarkEditor({
                visible: false,
                projectId: null,
                issueCategory: null,
                projectLabel: "",
                remark: ""
            });
        } catch (error) {
            logger.error("Failed to save issue remark:", error);
            message.error(getApiErrorMessage(error, "Failed to save remark"));
        } finally {
            setIssueRemarkSaving(false);
        }
    }, [issueRemarkEditor.issueCategory, issueRemarkEditor.projectId, message]);

    const closeIssueEventSource = useCallback((eventSource = eventSourceRef.current) => {
        if (!eventSource) {
            return;
        }

        closingEventSourcesRef.current.add(eventSource);
        eventSource.close();

        if (eventSourceRef.current === eventSource) {
            eventSourceRef.current = null;
        }
    }, []);

    // Cleanup EventSource on component unmount
    useEffect(() => {
        return () => {
            closeIssueEventSource();
        };
    }, [closeIssueEventSource]);

    // Auto-scan function - scans all categories for the current active tab
    const autoScanIssues = useCallback(() => {
        // Get categories for the current active tab
        const categoriesToScan = CATEGORY_GROUPS[activeIssuesTab].categories;

        if (categoriesToScan.length === 0) {
            return;
        }

        // Always close existing EventSource and reset loading ref when switching tabs
        // This allows the new scan to start immediately
        closeIssueEventSource();
        isLoadingRef.current = false;

        // Reset UI state for new scan
        setIsScanning(false);
        setIssuesData(null);
        setProgress({ current: 0, total: 0 });
        setScanProgress({});

        // Start new scan
        isLoadingRef.current = true;
        setIsScanning(true);

        // Set all categories to scanning status
        const initialProgress = {};
        categoriesToScan.forEach(code => {
            initialProgress[code] = { status: 'scanning', count: 0 };
        });
        setScanProgress(initialProgress);

        const queryParams = buildIssueScannerQueryParams({
            categories: categoriesToScan,
            dateRange: getDateRange(),
            clientId,
            clientContactId,
            picId,
            vendorId,
            assignedUser,
            assignedUserEmail,
        });

        // Determine endpoint based on context
        const streamPath = (myProjects || isMyProjects)
            ? '/project/my-projects-issues/stream'
            : '/project/projects-issues/stream';

        // Create EventSource URL
        const apiUrl = process.env.VITE_API_URL || process.env.VITE_NODE_API_URL;
        const eventSourceUrl = `${apiUrl}${streamPath}${queryParams}`;

        // Create EventSource
        const eventSource = createAuthorizedEventSource(eventSourceUrl);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = async (event) => {
            try {
                const sseMessage = JSON.parse(event.data);

                switch (sseMessage.type) {
                    case 'start':
                        setProgress({ current: 0, total: sseMessage.total });
                        break;

                    case 'progress':
                        setProgress({
                            current: sseMessage.current,
                            total: sseMessage.total
                        });
                        break;

                    case 'complete': {
                        terminalEventSourcesRef.current.add(eventSource);
                        const data = sseMessage.data;
                        let issueGroups = data.issueGroups || [];
                        try {
                            issueGroups = await fetchIssueRemarksForGroups(issueGroups, categoriesToScan);
                        } catch (remarkError) {
                            logger.error("Failed to load issue remarks:", remarkError);
                            message.warning("Issue scan completed, but remarks could not be loaded.");
                        }
                        const totalIssuesFound = data.totalIssuesFound || 0;
                        const totalProjectsChecked = data.totalProjectsChecked || 0;

                        // Update progress for each category with results
                        const finalProgress = {};
                        categoriesToScan.forEach(code => {
                            const issueGroup = issueGroups.find(g => g.category === code);
                            finalProgress[code] = {
                                status: 'completed',
                                count: issueGroup?.count || 0
                            };
                        });
                        setScanProgress(finalProgress);

                        // Set the final combined results
                        setIssuesData({
                            issueGroups: issueGroups,
                            totalIssuesFound: totalIssuesFound,
                            totalProjectsChecked: totalProjectsChecked,
                            hasErrors: false,
                            errorCount: 0
                        });
                        setHasScanned(true);
                        setIsScanning(false);
                        isLoadingRef.current = false;
                        setProgress({ current: 0, total: 0 });

                        // Close connection
                        closeIssueEventSource(eventSource);
                        break;
                    }

                    case 'error': {
                        terminalEventSourcesRef.current.add(eventSource);
                        const errorMsg = sseMessage.message || 'Scan failed';

                        // Mark all categories as failed
                        const errorProgress = {};
                        categoriesToScan.forEach(code => {
                            errorProgress[code] = {
                                status: 'completed',
                                count: 0,
                                error: true,
                                errorMessage: errorMsg
                            };
                        });
                        setScanProgress(errorProgress);

                        // Set error state
                        setIssuesData({
                            issueGroups: [],
                            totalIssuesFound: 0,
                            hasErrors: true,
                            errorCount: categoriesToScan.length,
                            globalError: errorMsg
                        });
                        setHasScanned(true);
                        setIsScanning(false);
                        isLoadingRef.current = false;

                        // Close connection
                        closeIssueEventSource(eventSource);

                        message.error(`Scan failed: ${errorMsg}`);
                        break;
                    }
                    default:
                        break;
                }
            } catch (parseError) {
                // Error parsing SSE message - ignore
            }
        };

        eventSource.onerror = (_error) => {
            if (
                closingEventSourcesRef.current.has(eventSource) ||
                terminalEventSourcesRef.current.has(eventSource) ||
                eventSourceRef.current !== eventSource
            ) {
                return;
            }

            setIsScanning(false);
            isLoadingRef.current = false;
            setProgress({ current: 0, total: 0 });

            // Close connection
            closeIssueEventSource(eventSource);

            message.error("Connection error. Please try again.");
        };
    }, [activeIssuesTab, assignedUser, assignedUserEmail, clientContactId, clientId, closeIssueEventSource, fetchIssueRemarksForGroups, getDateRange, isMyProjects, message, myProjects, picId, vendorId]);

    // Auto-trigger scan when tab or date filter changes
    useEffect(() => {
        // Only auto-scan when we're on the issues tab
        if (activeTab === 'issues') {
            autoScanIssues();
        }
    }, [activeTab, activeIssuesTab, dateFilterType, customDateRange, autoScanIssues]);

    const handleMenuClick = (action, record) => {
        switch (action) {
            case "Edit":
                // Navigate to edit page or open edit modal
                message.info(
                    `Edit functionality not yet implemented for: ${record.name}`
                );
                break;
            case "Duplicate":
                // Show duplicate modal
                setProjectToDuplicate(record);
                setDuplicateModalVisible(true);
                break;
            case "Delete":
                // Show design system delete confirmation modal
                setProjectToDelete(record);
                setDeleteModalVisible(true);
                break;
            default:
                message.info(`${action} project: ${record.name}`);
        }
    };

    const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
        if (extra?.action !== "sort") {
            return;
        }

        const nextSort = getProjectListSortFromTableSorter(sorter, activeTab);
        if (!nextSort) {
            return;
        }

        setSortState({ ...nextSort, currencyMode });

        if (!externalOnSortChange) {
            setInternalPagination((currentPagination) => ({
                ...currentPagination,
                page: 1,
            }));
        }
    }, [activeTab, externalOnSortChange, setSortState, currencyMode]);

    useEffect(() => {
        if (sortState.orderBy && sortState.currencyMode !== currencyMode) {
            setSortState({ ...sortState, currencyMode });
        }
    }, [currencyMode, sortState, setSortState]);

    const handleExportConfirm = async (location = exportLocation) => {
        try {
            setExportLoading(true);
            setExportProgress(0);
            setExportError(null);

            // Dynamic progress simulation
            const updateProgress = (progress, delay = 300) => {
                return new Promise(resolve => {
                    setTimeout(() => {
                        setExportProgress(progress);
                        resolve();
                    }, delay);
                });
            };

            // Custom exporters retain their existing progress behavior.
            if (onEnhancedExport) {
                await updateProgress(5, 100);
                await updateProgress(15, 200);
                await updateProgress(25, 300);
                await updateProgress(40, 400);
                await updateProgress(60, 200); // Starting export...

                // Call the enhanced export function
                await onEnhancedExport(location);

                await updateProgress(85, 200); // Finalizing...
                await updateProgress(95, 200); // Almost done...
                await updateProgress(100, 300); // Complete!

                // Small delay to show completion
                setTimeout(() => {
                    setExportModalVisible(false);
                    setExportLoading(false);
                    setExportProgress(0);
                }, 800);
            } else {
                // Build query parameters from component state
                const params = new URLSearchParams();

                // Add type filter based on tab
                if (activeTab && activeTab !== "all") {
                    params.append("type", activeTab);
                }

                // Add context filters
                if (clientId) params.append("client_id", clientId);
                if (clientContactId) params.append("clientPic", clientContactId);
                if (picId) params.append("pic_id", picId);
                if (vendorId) params.append("vendor", vendorId);
                if (assignedUser) params.append("assigned_user", assignedUser);
                if (assignedUserEmail) params.append("assigned_user_email", assignedUserEmail);

                // Add retainer filter
                if (retainerFilter === "retainer") {
                    params.append("retainer", "true");
                } else if (retainerFilter === "non-retainer") {
                    params.append("retainer", "false");
                }

                // Add search filter
                if (searchText && searchText.trim()) {
                    params.append("search", searchText.trim());
                }

                // Add date range filter
                if (dateRange && dateRange[0]) {
                    params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
                }
                if (dateRange && dateRange[1]) {
                    params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
                }

                // Add my_projects filter
                if (myProjects || isMyProjects) {
                    params.append("my_projects", "true");
                }

                // Add other filters
                if (departmentFilter && departmentFilter.length > 0) {
                    params.append("department_ids", departmentFilter.join(","));
                }
                if (projectTypeFilter && projectTypeFilter.length > 0) {
                    params.append("classification_ids", projectTypeFilter.join(","));
                }
                if (industryFilter && industryFilter.length > 0) {
                    params.append("industry_ids", industryFilter.join(","));
                }

                // Add status filters
                if (statusFilter && statusFilter.length > 0) {
                    if (activeTab === "lead") {
                        const leadStatuses = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
                        if (statusFilter.length < leadStatuses.length) {
                            params.append("lead_list", statusFilter.join(","));
                        }
                    } else {
                        params.append("status_list", statusFilter.join(","));
                    }
                }

                // Add export target parameter
                params.append("_target", location);

                const reportType = ['lead', 'internal'].includes(activeTab) ? activeTab : 'confirmed';
                const endpoint = `/report/projects/${reportType}/exports`;
                const response = await nodeAPI.post(`${endpoint}?${params.toString()}`);
                const jobId = response.data?.data?.jobId || response.data?.jobId;
                if (!jobId) {
                    throw new Error('Failed to start export. No job ID returned.');
                }

                // Keep the endpoint with the job even if the user changes tabs.
                setExportJob({ jobId, endpoint, reportType });
                setExportModalVisible(false);
                setExportLoading(false);
            }
        } catch (error) {
            logger.error("Export failed:", error);
            setExportError(error.message || "Export failed. Please try again.");
            setExportLoading(false);
            setExportProgress(0);

            message.error({
                content: "Export failed. Please try again.",
                key: "export",
            });
        }
    };

    const handleExportComplete = (results) => {
        try {
            if (results?.fileUrl) {
                notification.success({
                    message: 'Export Complete',
                    description: (
                        <a href={results.fileUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#1b1b1b', textDecoration: 'underline' }}>
                            Open in Google Drive
                        </a>
                    ),
                    key: 'export',
                    duration: 0,
                });
                window.open(results.fileUrl, '_blank', 'noopener,noreferrer');
            } else if (results?.downloadBuffer) {
                const binaryString = atob(results.downloadBuffer);
                const bytes = Uint8Array.from(binaryString, (character) => character.charCodeAt(0));
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = results.filename || `Projects_${exportJob.reportType}_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                message.success({ content: 'Export downloaded successfully!', key: 'export' });
            }
        } catch (error) {
            logger.error('Error downloading export:', error);
            message.error({ content: 'Failed to download file. Please try again.', key: 'export' });
        } finally {
            setExportJob(null);
        }
    };

    const handleExportCancel = () => {
        if (exportLoading) {
            message.warning('Please wait for the export to complete before closing.');
            return;
        }
        setExportModalVisible(false);
        setExportError(null);
        setExportProgress(0);
    };

    const handleMonthlyExport = async (target) => {
        if (!selectedMonth || !selectedYear) {
            message.error('Please select month and year');
            return;
        }

        try {
            setMonthlyExportLoading(true);
            setMonthlyExportProgress(0);
            setMonthlyExportError(null);

            // Show loading message
            message.loading({
                content: "Generating monthly report...",
                key: "monthly-export",
            });

            // Dynamic progress simulation
            const updateProgress = (progress, delay = 300) => {
                return new Promise(resolve => {
                    setTimeout(() => {
                        setMonthlyExportProgress(progress);
                        resolve();
                    }, delay);
                });
            };

            // Simulate realistic monthly report generation steps
            await updateProgress(8, 100);   // Initializing report...
            await updateProgress(18, 200);  // Collecting data...
            await updateProgress(28, 300);  // Processing monthly data...
            await updateProgress(38, 400);  // Calculating metrics...

            // Use nodeAPI for monthly export to include authentication
            const params = {
                month: selectedMonth,
                year: selectedYear,
                _target: target
            };

            // Add current filter state to params
            if (searchText) params.search = searchText;
            if (departmentFilter.length) params.department_ids = departmentFilter.join(',');
            if (statusFilter.length) params.status_list = statusFilter.join(',');
            if (projectTypeFilter.length) params.classification_ids = projectTypeFilter.join(',');
            if (industryFilter.length) params.industry_ids = industryFilter.join(',');
            if (retainerFilter !== 'all') params.retainer = retainerFilter === 'retainer';
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.start_date = dateRange[0].format('YYYY-MM-DD');
                params.end_date = dateRange[1].format('YYYY-MM-DD');
            }

            // Add project type based on active tab
            if (activeTab === 'confirmed') {
                params.type = 'CONFIRMED';
            } else if (activeTab === 'lead') {
                params.type = 'LEAD';
            } else if (activeTab === 'prospect') {
                params.type = 'PROSPECT';
            } else if (activeTab === 'internal') {
                params.type = 'INTERNAL';
            }

            await updateProgress(55, 200); // Preparing export...

            // Call API with authentication
            const response = await nodeAPI.get('/report/projects/monthly/export', {
                params,
                responseType: target === 'local' ? 'blob' : 'json' // Different response type based on target
            });

            await updateProgress(80, 200); // Processing response...

            if (target === 'local') {
                // Create download link for local export
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
                const monthName = monthNames[selectedMonth - 1];
                link.setAttribute('download', `Project_Monthly_Report_${monthName}_${selectedYear}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                await updateProgress(95, 200); // Finalizing download...
                await updateProgress(100, 300); // Complete!

                // Small delay to show completion
                setTimeout(() => {
                    setMonthlyExportModalVisible(false);
                    setMonthlyExportLoading(false);
                    setMonthlyExportProgress(0);
                }, 800);

                message.success({
                    content: "Monthly report downloaded successfully!",
                    key: "monthly-export",
                });
            } else if (target === 'google') {
                // Handle Google Drive response with notification
                // Check for both direct and nested response structures
                let responseData = response.data;
                if (responseData?.data) {
                    responseData = responseData.data;
                }

                await updateProgress(95, 200); // Uploading to Google Drive...
                await updateProgress(100, 300); // Upload complete!

                if (responseData && responseData.success && (responseData.directLink || responseData.webViewLink)) {
                    const driveLink = responseData.directLink || responseData.webViewLink;

                    // Small delay to show completion
                    setTimeout(() => {
                        setMonthlyExportModalVisible(false);
                        setMonthlyExportLoading(false);
                        setMonthlyExportProgress(0);
                    }, 800);

                    notification.success({
                        message: 'Monthly Report Export Complete',
                        description: (
                            <div>
                                <div style={{ marginBottom: 8 }}>
                                    Monthly report uploaded successfully to Google Drive!
                                </div>
                                <a
                                    href={driveLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#1890ff',
                                        fontWeight: 'bold',
                                        textDecoration: 'underline'
                                    }}
                                    onClick={() => {
                                        window.open(driveLink, '_blank');
                                    }}
                                >
                                    🔗 Open in Google Drive
                                </a>
                            </div>
                        ),
                        key: "monthly-export",
                        duration: 0,
                        placement: 'topRight',
                        closable: true
                    });
                } else {
                    // Small delay to show completion
                    setTimeout(() => {
                        setMonthlyExportModalVisible(false);
                        setMonthlyExportLoading(false);
                        setMonthlyExportProgress(0);
                    }, 800);

                    message.success({
                        content: "Monthly report uploaded to Google Drive successfully!",
                        key: "monthly-export",
                    });
                }
            }
        } catch (error) {
            logger.error('Monthly export failed:', error);
            setMonthlyExportError(error.message || "Monthly export failed. Please try again.");
            setMonthlyExportLoading(false);
            setMonthlyExportProgress(0);

            message.error({
                content: "Monthly export failed. Please try again.",
                key: "monthly-export",
            });
        }
    };

    const handleMonthlyExportCancel = () => {
        if (monthlyExportLoading) {
            message.warning('Please wait for the export to complete before closing.');
            return;
        }
        setMonthlyExportModalVisible(false);
        setSelectedMonth(undefined);
        setSelectedYear(new Date().getFullYear());
        setMonthlyExportError(null);
        setMonthlyExportProgress(0);
    };

    // Handle duplicate confirmation
    const handleDuplicateConfirm = async () => {
        if (!projectToDuplicate) return;

        setDuplicateLoading(true);
        try {
            // Use project_id (actual database ID) not id (which is the generated_id like "2508-1983")
            const projectId =
                projectToDuplicate.project_id || projectToDuplicate.id;
            const response = await nodeAPI.post(
                `/project/${projectId}/duplicate`
            );

            // Check for successful response (status code 1 or OK)
            if (
                response.data &&
                (response.data.statusCode === 1 ||
                    response.data.statusCode?.code === 1)
            ) {
                message.success("Project duplicated successfully");
                setDuplicateModalVisible(false);

                // Navigate to the new project detail page
                const newProjectId =
                    response.data.data?.project?.id ||
                    response.data.project?.id;
                if (newProjectId) {
                    window.location.href = `/project-management/project/${newProjectId}`;
                }
            } else {
                message.error(
                    response.data?.error || "Failed to duplicate project"
                );
            }
        } catch (error) {
            logger.error("Error duplicating project:", error);
            message.error("Failed to duplicate project");
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleDuplicateCancel = () => {
        setDuplicateModalVisible(false);
        setProjectToDuplicate(null);
    };

    // Delete project handlers
    const handleDeleteProject = async () => {
        if (!projectToDelete) return;

        setIsDeleting(true);
        setDeleteError(null); // Clear previous error
        try {
            await nodeAPI.delete(`/project/${projectToDelete.project_id}`);
            message.success({
                content: `Project "${projectToDelete.name}" deleted successfully`,
                duration: 3
            });

            // Close modal and reset state
            setDeleteModalVisible(false);
            setProjectToDelete(null);
            setDeleteError(null);

            // Refresh the project list
            if (externalOnRefresh) {
                externalOnRefresh();
            } else {
                fetchProjects();
            }
        } catch (error) {
            logger.error("Error deleting project:", error);

            // Display error message in modal instead of toast
            // Backend sends error in data.data.error format (via render helper)
            setDeleteError(getApiErrorMessage(error, "Failed to delete project"));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setProjectToDelete(null);
        setDeleteError(null);
    };

    // Helper function to format currency values
    const formatCurrencyValue = (
        value,
        currencySymbol = "$",
        isHighlighted = false
    ) => {
        if (!value && value !== 0) return <span style={{ fontSize: '12px' }}>-</span>;

        const absValue = Math.abs(value);
        const formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: absValue < 1000 ? 2 : 0,
            maximumFractionDigits: absValue < 1000 ? 2 : 0,
        }).format(absValue);

        // Use the currency symbol from database
        const symbol = currencySymbol || "$";
        const formattedValue =
            value < 0 ? `(${symbol}${formatted})` : `${symbol}${formatted}`;

        // Return with or without highlight wrapper
        if (isHighlighted) {
            return (
                <span
                    style={{
                        backgroundColor: "rgba(254, 210, 48, 0.1)",
                        padding: "2px 4px",
                        borderRadius: "2px",
                        display: "inline-block",
                        fontSize: '12px',
                    }}
                >
                    {formattedValue}
                </span>
            );
        }
        return <span style={{ fontSize: '12px' }}>{formattedValue}</span>;
    };

    const formatSgdCurrencyValue = (value) => {
        const amount = Number(value || 0);
        const absValue = Math.abs(amount);
        const formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: absValue < 1000 && absValue % 1 !== 0 ? 2 : 0,
            maximumFractionDigits: 2,
        }).format(absValue);

        return amount < 0 ? `-S$${formatted}` : `S$${formatted}`;
    };

    const showCreditExposure = Boolean(clientId) && activeTab === "confirmed";

    const getCreditUsagePercent = (amount) => {
        const limitAmount = Number(creditLimitAmountSgd || 0);
        if (!limitAmount || limitAmount <= 0) {
            return null;
        }

        return Math.min(100, Math.max(0, Math.round((Number(amount || 0) / limitAmount) * 100)));
    };

    const renderCreditUsedCell = (_, record) => {
        const exposure = record.creditExposure || {};
        const creditUsed = Number(exposure.netCreditUsedSgd || 0);
        const percent = getCreditUsagePercent(creditUsed);

        return (
            <div className="credit-used-cell">
                <span className="credit-used-amount">
                    {formatSgdCurrencyValue(creditUsed)}
                </span>
                <div className="credit-used-bar" aria-hidden="true">
                    <div
                        className="credit-used-bar-fill"
                        style={{ width: `${percent ?? 0}%` }}
                    />
                </div>
                <span className="credit-used-percent">
                    {percent !== null ? `${percent}%` : "-"}
                </span>
            </div>
        );
    };

    const renderCreditExposureBreakdown = (record) => {
        const exposure = record.creditExposure || {};
        const creditUsed = Number(exposure.netCreditUsedSgd || 0);
        const clientInvoiced = Number(
            exposure.clientInvoicedSgd ?? exposure.clientInvoicedRecoveredSgd ?? 0
        );
        const clientPaidRecovered = Number(
            exposure.clientPaidRecoveredSgd ?? exposure.clientInvoicedRecoveredSgd ?? 0
        );
        const percent = getCreditUsagePercent(creditUsed);

        return (
            <div className="project-credit-breakdown">
                <div className="project-credit-breakdown-title">
                    Credit usage breakdown
                </div>
                <div className="project-credit-breakdown-items">
                    <div className="project-credit-breakdown-item paid">
                        <span>Vendor paid advances</span>
                        <strong>{formatSgdCurrencyValue(exposure.paidAdvancesSgd)}</strong>
                    </div>
                    <div className="project-credit-breakdown-item bills">
                        <span>Vendor bills received</span>
                        <strong>{formatSgdCurrencyValue(exposure.billsReceivedSgd)}</strong>
                    </div>
                    <div className="project-credit-breakdown-item uninvoiced">
                        <span>Uninvoiced vendor cost</span>
                        <strong>{formatSgdCurrencyValue(exposure.uninvoicedVendorCostSgd)}</strong>
                    </div>
                    <div className="project-credit-breakdown-item invoiced">
                        <span>Client invoiced</span>
                        <strong>{formatSgdCurrencyValue(clientInvoiced)}</strong>
                    </div>
                    <div className="project-credit-breakdown-item recovered">
                        <span>Client paid / recovered</span>
                        <strong>{formatSgdCurrencyValue(-clientPaidRecovered)}</strong>
                    </div>
                    <div className="project-credit-breakdown-item net">
                        <span>Net credit used</span>
                        <strong>{formatSgdCurrencyValue(creditUsed)}</strong>
                    </div>
                    <div className="project-credit-breakdown-percent">
                        {percent !== null ? `${percent}%` : "-"}
                    </div>
                </div>
            </div>
        );
    };

    const hasCurrencyValue = (value) => value !== undefined && value !== null && value !== "";
    const firstCurrencyValue = (...values) => values.find(hasCurrencyValue);
    const getProjectCurrencySymbol = (record) => record.currencySymbol || record.currency_symbol || "$";
    const getCurrencyDisplay = (record, values) => {
        if (currencyMode === PROJECT_CURRENCY_MODES.LOCAL) {
            return {
                value: firstCurrencyValue(values.local, values.usd, values.sgd),
                symbol: getProjectCurrencySymbol(record),
                highlight: record.currency_iso && record.currency_iso !== "SGD",
            };
        }

        if (currencyMode === PROJECT_CURRENCY_MODES.USD) {
            return {
                value: firstCurrencyValue(values.usd, values.sgd, values.local),
                symbol: "$",
                highlight: false,
            };
        }

        return {
            value: firstCurrencyValue(values.sgd, values.usd, values.local),
            symbol: "S$",
            highlight: false,
        };
    };

    const menuItems = (record) => {
        // Check user roles with proper role hierarchy - 
        const hasEditPermission = (
            roleService.hasRole(ROLES.ROLE_TEAM_LEADER) ||
            roleService.hasRole(ROLES.ROLE_MARCOM) ||
            roleService.hasRole(ROLES.ROLE_FINANCE)
        );

        // Duplicate is only enabled when on the main Projects tab
        // It's disabled on "My Projects" (g='my-project' or null) and other tabs
        // In our React app, we check if the title is "PROJECTS" (not "MY PROJECTS")
        const isDuplicateEnabled = title === "PROJECTS";

        const items = [];

        // Edit - always disabled (line 186: class="disabled")
        if (hasEditPermission) {
            items.push({
                key: "edit",
                label: (
                    <span style={{ color: "#999" }}>
                        <i
                            className="fa fa-pencil-square-o"
                            style={{ marginRight: 8 }}
                        />
                        Edit
                    </span>
                ),
                disabled: true, // Always disabled
                onClick: () => handleMenuClick("Edit", record),
            });
        }

        // Duplicate - it checks if we're on the projects list page (g='projects')
        items.push({
            key: "duplicate",
            label: (
                <span style={!isDuplicateEnabled ? { color: "#999" } : {}}>
                    <i className="fa fa-files-o" style={{ marginRight: 8 }} />
                    Duplicate
                </span>
            ),
            disabled: !isDuplicateEnabled,
            onClick: () =>
                isDuplicateEnabled
                    ? handleMenuClick("Duplicate", record)
                    : null,
        });

        // Delete - only for users with permission
        if (hasEditPermission) {
            items.push({
                key: "delete",
                label: (
                    <span className="delete-menu-item" style={{ color: "#dc3545" }}>
                        <i
                            className="fa fa-trash-o"
                            style={{ marginRight: 8, color: "#dc3545" }}
                        />
                        Delete
                    </span>
                ),
                danger: true,
                onClick: () => handleMenuClick("Delete", record),
            });
        }

        return items;
    };

    // Define columns based on active tab
    const getColumns = () => {
        const baseColumns = [
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                width: 60,
                sorter: (a, b) => a.id.localeCompare(b.id),
                render: (id) => (
                    <span style={{ fontSize: '12px' }}>{id}</span>
                ),
            },
        ];

        // Add Created At column for Prospect tab right after ID
        if (activeTab === "prospect") {
            baseColumns.push({
                title: "Created At",
                dataIndex: "created_at",
                key: "created_at",
                align: "center",
                width: 80,
                sorter: (a, b) => {
                    const dateA = a.created_at
                        ? new Date(a.created_at)
                        : new Date(0);
                    const dateB = b.created_at
                        ? new Date(b.created_at)
                        : new Date(0);
                    return dateA - dateB;
                },
                render: (date) => {
                    if (!date) return <span style={{ fontSize: '12px' }}>-</span>;
                    // Format date as DD/MM/YY
                    const d = new Date(date);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = String(d.getFullYear()).slice(-2);
                    return <span style={{ fontSize: '12px' }}>{`${day}/${month}/${year}`}</span>;
                },
            });
        }

        // Add Project Name column
        baseColumns.push(
            {
                title: "Project Name",
                dataIndex: "name",
                key: "name",
                width: 140,
                render: (text, record) => (
                    <div><a
                        href={`/project-management/project/${
                            record.project_id || record.id
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="table-link"
                    >
                        {text} <ExportOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
                    </a>
                    <OnboardingProgress progress={onboarding.projects[record.project_id || record.id]} label="Project onboarding" /></div>
                ),
            }
        );

        // Only add status column if NOT on prospect tab
        if (activeTab !== "prospect") {
            const statusSortField = activeTab === "lead"
                ? PROJECT_LIST_SORT_FIELDS.LEAD_STATUS
                : PROJECT_LIST_SORT_FIELDS.STATUS;

            baseColumns.push({
                title: activeTab === "lead" ? "Lead Status" : "Project Status",
                dataIndex: activeTab === "lead" ? "lead" : "status",
                key: "status",
                width: activeTab === "lead" ? 200 : 80,
                align: "center",
                sorter: true,
                sortOrder: getProjectListSortOrderForColumn(sortState, statusSortField),
                render: (status, record) => {
                    // Use lead field for lead tab, otherwise use status
                    const displayStatus =
                        activeTab === "lead" ? record.lead : status;

                    const getStatusStyle = (status) => {
                        // Lead specific statuses - always use light gray background with black text
                        if (activeTab === "lead") {
                            // Always return light gray background for lead statuses
                            return {
                                backgroundColor: "#f8f9fa", // Light gray
                                color: "#000000", // Black text
                            };
                        }

                        // Project statuses (for other tabs)
                        const statusLower = (status || "")
                            .toLowerCase()
                            .replace(/\s+/g, "-");

                        // Project statuses (for other tabs)
                        switch (statusLower) {
                            case "on-going":
                            case "ongoing":
                            case "active":
                                return {
                                    backgroundColor: "rgb(40, 167, 69)", // #28a745
                                    color: "#fff",
                                };
                            case "in-planning":
                            case "planning":
                            case "in planning":
                                return {
                                    backgroundColor: "rgb(23, 162, 184)", // #17a2b8
                                    color: "#fff",
                                };
                            case "completed":
                            case "finished":
                            case "done":
                                return {
                                    backgroundColor: "rgb(179, 215, 65)", // #b3d741
                                    color: "#343a40",
                                };
                            case "cancelled":
                            case "canceled":
                            case "rejected":
                                return {
                                    backgroundColor: "rgb(255, 54, 54)", // #ff3636
                                    color: "#fff",
                                };
                            case "on-hold":
                            case "hold":
                            case "paused":
                                return {
                                    backgroundColor: "rgb(250, 209, 51)", // #fad133
                                    color: "#343a40",
                                };
                            case "draft":
                            case "pending":
                                return {
                                    backgroundColor: "rgb(238, 238, 238)", // #eeeeee
                                    color: "#343a40",
                                };
                            default:
                                return {
                                    backgroundColor: "rgb(74, 74, 74)", // #4a4a4a
                                    color: "#fff",
                                };
                        }
                    };

                    const statusStyle = getStatusStyle(displayStatus);

                    // Use database value for lead status display
                    let cleanStatus = displayStatus;
                    if (activeTab === "lead") {
                        if (
                            displayStatus === null ||
                            displayStatus === undefined ||
                            displayStatus === ""
                        ) {
                            // If no lead status, show a dash
                            return <span>—</span>;
                        }

                        // Use project_lead_status.name from database if available
                        if (record.project_lead_status?.name) {
                            cleanStatus = `${displayStatus} - ${record.project_lead_status.name}`;
                        } else {
                            // Fallback: just show the number
                            cleanStatus = displayStatus;
                        }
                    }

                    // Don't show pill for empty status in non-lead tabs
                    if (!activeTab === "lead" && !cleanStatus) {
                        return <span>—</span>;
                    }

                    return (
                        <span
                            style={{
                                backgroundColor: statusStyle.backgroundColor,
                                color: statusStyle.color,
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "11px",
                                fontWeight:
                                    activeTab === "lead" ? "400" : "600", // Normal weight for lead, bold for others
                                textTransform:
                                    activeTab === "lead"
                                        ? "none"
                                        : "capitalize",
                                display: "inline-block",
                                whiteSpace: activeTab === "lead" ? "normal" : "nowrap",
                            }}
                        >
                            {cleanStatus}
                        </span>
                    );
                },
            });
        }

        // Add PIC column
        baseColumns.push({
            title: "PIC",
            dataIndex: "pic",
            key: "pic",
            width: 80,
            sorter: true,
            sortOrder: getProjectListSortOrderForColumn(sortState, PROJECT_LIST_SORT_FIELDS.PIC),
            render: (text, record) =>
                record.pic_id ? (
                    <Link
                        to={`/project-management/pic/${record.pic_id}`}
                        className="table-link"
                    >
                        {text}
                    </Link>
                ) : (
                    <span>{text || "-"}</span>
                ),
        });

        // Vendor-specific financial columns (only on vendor detail page)
        if (vendorId) {
            baseColumns.push({
                title: "Planned Amount",
                dataIndex: "vendor_planned_usd",
                key: "vendor_planned_usd",
                align: "center",
                width: 55,
                sorter: (a, b) => (a.vendor_planned_usd || 0) - (b.vendor_planned_usd || 0),
                render: (value) => {
                    if (value === null || value === undefined) return '-';
                    return formatCurrencyValue(value, '$', false);
                },
            });
            baseColumns.push({
                title: "Billed Amount",
                dataIndex: "vendor_billed_usd",
                key: "vendor_billed_usd",
                align: "center",
                width: 55,
                sorter: (a, b) => (a.vendor_billed_usd || 0) - (b.vendor_billed_usd || 0),
                render: (value) => {
                    if (value === null || value === undefined) return '-';
                    return formatCurrencyValue(value, '$', false);
                },
            });
        }

        // Add conditional columns based on tab
        if (activeTab === "lead") {
            // For Lead tab, add Sent Date column
            baseColumns.push({
                title: "Sent Date",
                dataIndex: "proposal_date",
                key: "proposal_date",
                align: "center",
                width: 80,
                sorter: (a, b) => {
                    const dateA = a.proposal_date
                        ? new Date(a.proposal_date)
                        : new Date(0);
                    const dateB = b.proposal_date
                        ? new Date(b.proposal_date)
                        : new Date(0);
                    return dateA - dateB;
                },
                render: (date) => {
                    if (!date) return <span style={{ fontSize: '12px' }}>-</span>;
                    // Format date as DD/MM/YY
                    const d = new Date(date);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = String(d.getFullYear()).slice(-2);
                    return <span style={{ fontSize: '12px' }}>{`${day}/${month}/${year}`}</span>;
                },
            });
        }
        // Note:
        // - Lead tab gets Sent Date column
        // - Prospect tab gets Created At column (added earlier after ID)
        // - Internal tab has no additional column

        return baseColumns;
    };

    const columns = [
        ...getColumns(),
        ...(showCreditExposure ? [
            {
                title: (
                    <Space>
                        <span key="label">Credit Used</span>
                        <Tooltip key="info" title="Vendor-side client credit exposure net of actual client payments received">
                            <InfoCircleOutlined style={{ fontSize: 14, color: "#8c8c8c" }} />
                        </Tooltip>
                    </Space>
                ),
                dataIndex: "creditExposure",
                key: "creditExposure",
                align: "center",
                width: 86,
                render: renderCreditUsedCell,
            },
        ] : []),
        {
            title: "Docs.",
            dataIndex: "docs",
            key: "docs",
            align: "center",
            width: 60,
            render: (_, record) => {
                // Always show both icons, just change their appearance based on availability
                return (
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                        }}
                    >
                        {/* Project Plan icon - always show */}
                        {record.plan_filepath ? (
                            <Tooltip
                                key="project-plan"
                                title={record.plan_filename || "Project Plan"}
                            >
                                <button
                                    type="button"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        try {
                                            // Show loading message
                                            const hideLoading = message.loading(
                                                "Preparing download...",
                                                0
                                            );

                                            // Get presigned URL from backend
                                            const response = await nodeAPI.get(
                                                `/project/${record.id || 0}/files/${encodeURIComponent(record.plan_filepath)}/download`,
                                                {
                                                    params: {
                                                        name: record.plan_filename,
                                                    },
                                                }
                                            );

                                            hideLoading();

                                            if (
                                                response.data &&
                                                response.data.data &&
                                                response.data.data.downloadUrl
                                            ) {
                                                // Create a temporary link and trigger download
                                                const link =
                                                    document.createElement("a");
                                                link.href =
                                                    response.data.data.downloadUrl;
                                                link.download =
                                                    record.plan_filename ||
                                                    "project-plan";
                                                link.target = "_blank";
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);

                                                message.success(
                                                    "Download started"
                                                );
                                            } else {
                                                message.error(
                                                    "Failed to get download URL"
                                                );
                                            }
                                        } catch (error) {
                                            logger.error(
                                                "Download error:",
                                                error
                                            );
                                            message.error(
                                                "Failed to download file"
                                            );
                                        }
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#1b1b1b",
                                        padding: 0,
                                    }}
                                >
                                    <i
                                        className="fa fa-file-o"
                                        style={{ fontSize: "14px" }}
                                    />
                                </button>
                            </Tooltip>
                        ) : (
                            <Tooltip key="project-plan" title="No Project Plan">
                                <i
                                    className="fa fa-file-o"
                                    style={{
                                        fontSize: "14px",
                                        color: "#dc3545",
                                        opacity: 0.65,
                                        cursor: "not-allowed",
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* Google Drive icon - always show */}
                        {record.plan_url ? (
                            <Tooltip key="google-drive" title="Google Drive">
                                <a
                                    href={record.plan_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        cursor: "pointer",
                                        color: "#1b1b1b",
                                    }}
                                >
                                    <i
                                        className="fa fa-file-text-o"
                                        style={{ fontSize: "14px" }}
                                    />
                                </a>
                            </Tooltip>
                        ) : (
                            <Tooltip key="google-drive" title="No Google Drive">
                                <i
                                    className="fa fa-file-text-o"
                                    style={{
                                        fontSize: "14px",
                                        color: "#dc3545",
                                        opacity: 0.65,
                                        cursor: "not-allowed",
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <Space>
                    <span key="label">Est. Billings</span>
                    <Tooltip key="info" title="Manual estimation entered when creating/editing the project">
                        <InfoCircleOutlined
                            style={{ fontSize: 14, color: "#8c8c8c" }}
                        />
                    </Tooltip>
                </Space>
            ),
            dataIndex: "estimatedBilling",
            key: "estimatedBilling",
            align: "right",
            width: 80,
            render: (value, record) => {
                const revenueDisplay = getCurrencyDisplay(record, {
                    sgd: firstCurrencyValue(record.planned_revenue_sgd, record.rawRevenueSgd),
                    usd: firstCurrencyValue(record.planned_revenue_usd, record.rawRevenueUsd),
                    local: record.planned_revenue,
                });

                if (hasCurrencyValue(revenueDisplay.value)) {
                    return formatCurrencyValue(
                        revenueDisplay.value,
                        revenueDisplay.symbol,
                        revenueDisplay.highlight
                    );
                }

                if (!value) return "-";

                return <span style={{ fontSize: '12px' }}>{value}</span>;
            },
        },
        {
            title: "Total Invoiced",
            dataIndex: "totalInvoicedUsd",
            key: "totalInvoiced",
            align: "right",
            width: 80,
            render: (value, record) => {
                const totalInvoicedDisplay = getCurrencyDisplay(record, {
                    sgd: record.totalInvoicedSgd,
                    usd: record.totalInvoicedUsd,
                    local: record.totalInvoicedProjectCurrency,
                });

                if (hasCurrencyValue(totalInvoicedDisplay.value)) {
                    return formatCurrencyValue(
                        totalInvoicedDisplay.value,
                        totalInvoicedDisplay.symbol,
                        totalInvoicedDisplay.highlight
                    );
                }

                if (!value && value !== 0) return "-";

                return <span style={{ fontSize: '12px' }}>{value}</span>;
            },
        },
        {
            title: "Vendor Revenue",
            dataIndex: "vendorRevenueUsd",
            key: "vendorRevenue",
            align: "right",
            width: 80,
            render: (value, record) => {
                const vendorRevenueDisplay = getCurrencyDisplay(record, {
                    sgd: record.vendorRevenueSgd,
                    usd: record.vendorRevenueUsd,
                    local: record.vendorRevenueProjectCurrency,
                });

                if (hasCurrencyValue(vendorRevenueDisplay.value)) {
                    return formatCurrencyValue(
                        vendorRevenueDisplay.value,
                        vendorRevenueDisplay.symbol,
                        vendorRevenueDisplay.highlight
                    );
                }

                if (!value && value !== 0) return "-";

                return <span style={{ fontSize: '12px' }}>{value}</span>;
            },
        },
        {
            title: "Agency Revenue",
            dataIndex: "agencyRevenueUsd",
            key: "agencyRevenue",
            align: "right",
            width: 80,
            render: (value, record) => {
                const agencyRevenueDisplay = getCurrencyDisplay(record, {
                    sgd: record.agencyRevenueSgd,
                    usd: record.agencyRevenueUsd,
                    local: record.agencyRevenueProjectCurrency,
                });

                if (hasCurrencyValue(agencyRevenueDisplay.value)) {
                    return formatCurrencyValue(
                        agencyRevenueDisplay.value,
                        agencyRevenueDisplay.symbol,
                        agencyRevenueDisplay.highlight
                    );
                }

                if (!value && value !== 0) return "-";

                return <span style={{ fontSize: '12px' }}>{value}</span>;
            },
        },
        campaignResultsColumn(campaignAvailability),
        {
            title: "Finance",
            dataIndex: "finance",
            key: "finance",
            align: "center",
            width: 50,
            render: (_, record) => {
                const salesOrders = Array.isArray(record.sales_orders)
                    ? record.sales_orders
                    : (Array.isArray(record.project_sales_order) ? record.project_sales_order : []);
                const salesOrderCount = Number(record.salesOrderCount);
                const invoiceCount = Number(record.invoiceCount);
                const hasSalesOrderFlag = typeof record.hasSalesOrders === "boolean";
                const hasInvoiceFlag = typeof record.hasInvoices === "boolean";
                const hasSalesOrders = hasSalesOrderFlag
                    ? record.hasSalesOrders
                    : ((Number.isFinite(salesOrderCount) && salesOrderCount > 0) || salesOrders.length > 0);

                let hasAnyInvoices = hasInvoiceFlag
                    ? record.hasInvoices
                    : (Number.isFinite(invoiceCount) && invoiceCount > 0);
                if (!hasInvoiceFlag && !hasAnyInvoices && hasSalesOrders) {
                    hasAnyInvoices = salesOrders.some((pso) => (
                        pso.sales_order?.sales_order_invoice?.length > 0
                    ));
                }

                const vendorPlanningIncomplete = isProjectVendorPlanningIncomplete(record);

                // If no sales orders at all, show both file icons
                if (!hasSalesOrders) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center",
                            }}
                        >
                            <Tooltip key="sales-order" title="No Sales Order">
                                <i
                                    className="fa fa-file-o"
                                    style={{
                                        fontSize: "14px",
                                        color: "#dc3545",
                                        opacity: 0.65,
                                    }}
                                />
                            </Tooltip>
                            <Tooltip key="invoice" title="No Invoice">
                                <i
                                    className="fa fa-file-text-o"
                                    style={{
                                        fontSize: "14px",
                                        color: "#dc3545",
                                        opacity: 0.65,
                                    }}
                                />
                            </Tooltip>
                            {vendorPlanningIncomplete ? (
                                <Tooltip key="vendor-planning" title="Vendor Planning selected but not completed">
                                    <i
                                        className="fa fa-book"
                                        style={{
                                            fontSize: "14px",
                                            color: "#dc3545",
                                            opacity: 0.65,
                                        }}
                                    />
                                </Tooltip>
                            ) : null}
                        </div>
                    );
                }

                // If sales orders exist but no invoices on any of them
                const showNoInvoiceIcon = hasSalesOrders && !hasAnyInvoices;

                // Show icons only if there are issues
                if (showNoInvoiceIcon || vendorPlanningIncomplete) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                justifyContent: "center",
                            }}
                        >
                            {showNoInvoiceIcon ? (
                                <Tooltip key="invoice" title="No Invoice">
                                    <i
                                        className="fa fa-file-text-o"
                                        style={{
                                            fontSize: "14px",
                                            color: "#dc3545",
                                            opacity: 0.65,
                                        }}
                                    />
                                </Tooltip>
                            ) : null}
                            {vendorPlanningIncomplete ? (
                                <Tooltip key="vendor-planning" title="Vendor Planning selected but not completed">
                                    <i
                                        className="fa fa-book"
                                        style={{
                                            fontSize: "14px",
                                            color: "#dc3545",
                                            opacity: 0.65,
                                        }}
                                    />
                                </Tooltip>
                            ) : null}
                        </div>
                    );
                }

                // No issues - return empty string instead of null to prevent "0" from rendering
                return "";
            },
        },
        {
            title: "",
            key: "action",
            width: 50,
            align: "right",
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: menuItems(record),
                        className: "project-action-dropdown",
                    }}
                    trigger={["click"]}
                    placement="bottomRight"
                    overlayStyle={{ minWidth: 160 }}
                >
                    <Button
                        type="text"
                        className="btn-ellipsis"
                        style={{
                            cursor: "pointer",
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 0, // Squared button
                            minWidth: 32,
                            height: 32,
                        }}
                    >
                        <i
                            className="fa fa-ellipsis-v"
                            style={{ fontSize: 14 }}
                        />
                    </Button>
                </Dropdown>
            ),
        },
    ].map(column => {
        if (column.key === 'action') return column;
        const mapped = getProjectListSortFromTableSorter({ columnKey: column.key, order: 'ascend' }, activeTab);
        return mapped ? { ...column, sorter: true, sortOrder: getProjectListSortOrderForColumn(sortState, mapped.orderBy) } : column;
    });

    // Filter projects based on clientId or picId if provided
    const filteredProjects = projects.filter((project) => {
        // Search is handled by backend, no need to filter here
        const matchesSearch = true;

        // Check if DateIssue or FinanceIssue filters are active
        const hasDateIssue = statusFilter.includes('DateIssue');
        const hasFinanceIssue = statusFilter.includes('FinanceIssue');

        // If issue filters are active, the backend already filtered the results
        // So we should show all projects that came from the backend
        // (they are already filtered for the issue condition)
        let matchesStatus = true;
        if (!hasDateIssue && !hasFinanceIssue) {
            // Only apply local status filtering if no issue filters are active
            if (activeTab === "lead") {
                // For lead tab, check the lead field
                matchesStatus =
                    statusFilter.length === 0 ||
                    statusFilter.includes(String(project.lead));
            } else {
                // For other tabs, check the status field
                matchesStatus =
                    statusFilter.length === 0 ||
                    statusFilter.includes(project.status);
            }
        }
        // If DateIssue or FinanceIssue is active, matchesStatus stays true
        // because the backend already filtered for us

        // Project type and industry filters are handled by the backend. The
        // lightweight project list does not include those ID arrays, so
        // re-filtering here would hide valid backend results.
        const matchesProjectType = true;

        // Department filter is now handled by backend (PIC department OR time_spent users' departments)
        // Backend logic is different from project.department_ids, so skip client-side filtering
        const matchesDepartment = true;

        const matchesIndustry = true;
        const matchesClient = !clientId || project.client_id === clientId;
        const matchesPIC = !picId || project.pic_id === picId;

        return (
            matchesSearch &&
            matchesStatus &&
            matchesProjectType &&
            matchesDepartment &&
            matchesIndustry &&
            matchesClient &&
            matchesPIC
        );
    });

    const creditExposureRowKeys = showCreditExposure
        ? filteredProjects
            .filter((project) => Boolean(project.creditExposure))
            .map(getProjectTableRowKey)
        : [];
    const creditExposureRowKeySignature = creditExposureRowKeys.join("|");

    useEffect(() => {
        if (!showCreditExposure) {
            setExpandedCreditRowKeys([]);
            return;
        }

        const visibleCreditKeys = new Set(
            creditExposureRowKeySignature ? creditExposureRowKeySignature.split("|") : []
        );
        setExpandedCreditRowKeys((currentKeys) =>
            currentKeys.filter((key) => visibleCreditKeys.has(String(key)))
        );
    }, [creditExposureRowKeySignature, showCreditExposure]);

    // Build tab items - Issues tab shows on all pages
    const tabItems = [
        {
            key: "issues",
            label: "Issues",
            children: null,
        },
        {
            key: "confirmed",
            label: "Confirmed",
            children: null,
        },
        {
            key: "lead",
            label: "Lead",
            children: null,
        },
        ...(showProspectTab ? [{
            key: "prospect",
            label: "Prospect",
            children: null,
        }] : []),
        {
            key: "internal",
            label: "Internal",
            children: null,
        },
    ];

    return (
        <div className="projects-container">
            <div className="projects-header-section">
                <div className="projects-title-row">
                    <div className="header-left">
                        <h4>
                            {title}
                            {activeTab !== 'issues' && activeTab !== 'prospect' && (
                                <DownloadOutlined
                                    onClick={() => {
                                        if (onExport) {
                                            onExport();
                                        } else {
                                            setExportModalVisible(true);
                                        }
                                    }}
                                    style={{
                                        marginLeft: 12,
                                        fontSize: 16,
                                        cursor: "pointer",
                                        color: "#52c41a",
                                    }}
                                    title="Export Projects"
                                />
                            )}
                            {activeTab !== 'issues' && activeTab !== 'prospect' && onVendorBreakdownExport && (
                                <DownSquareOutlined
                                    onClick={onVendorBreakdownExport}
                                    style={{
                                        marginLeft: 8,
                                        fontSize: 16,
                                        cursor: "pointer",
                                        color: "#1890ff",
                                    }}
                                    title="Detailed Confirmed Report with Vendors"
                                />
                            )}
                            {/* HIDDEN: Monthly Report - not needed at the moment */}
                            {/* {activeTab !== 'issues' && activeTab !== 'prospect' && (
                                <CalendarOutlined
                                    onClick={() => {
                                        setMonthlyExportError(null);
                                        setMonthlyExportProgress(0);
                                        setMonthlyExportLoading(false);
                                        setMonthlyExportModalVisible(true);
                                    }}
                                    style={{
                                        marginLeft: 8,
                                        fontSize: 16,
                                        cursor: "pointer",
                                        color: "#ff9800",
                                    }}
                                    title="Export Monthly Report"
                                />
                            )} */}
                        </h4>
                    </div>
                </div>

                <div className="projects-controls-row">
                    <div className="controls-left">
                        <div className="currency-toggle">
                            <Button
                                className={`currency-btn ${
                                    currencyMode === PROJECT_CURRENCY_MODES.SGD ? "active" : ""
                                }`}
                                onClick={() => setCurrencyMode(PROJECT_CURRENCY_MODES.SGD)}
                            >
                                SGD
                            </Button>
                            <Button
                                className={`currency-btn ${
                                    currencyMode === PROJECT_CURRENCY_MODES.USD ? "active" : ""
                                }`}
                                onClick={() => setCurrencyMode(PROJECT_CURRENCY_MODES.USD)}
                            >
                                USD
                            </Button>
                            <Button
                                className={`currency-btn ${
                                    currencyMode === PROJECT_CURRENCY_MODES.LOCAL ? "active" : ""
                                }`}
                                onClick={() => setCurrencyMode(PROJECT_CURRENCY_MODES.LOCAL)}
                            >
                                Local CCY
                            </Button>
                        </div>
                    </div>
                    <div className="controls-right">
                        <Space size="middle">
                            <Button
                                className={
                                    retainerFilter === "non-retainer"
                                        ? "retainer-btn active"
                                        : "retainer-btn"
                                }
                                onClick={() =>
                                    setRetainerFilter(
                                        retainerFilter === "non-retainer"
                                            ? "all"
                                            : "non-retainer"
                                    )
                                }
                            >
                                NON-RETAINER
                            </Button>
                            <Button
                                className={
                                    retainerFilter === "retainer"
                                        ? "retainer-btn active"
                                        : "retainer-btn"
                                }
                                onClick={() =>
                                    setRetainerFilter(
                                        retainerFilter === "retainer"
                                            ? "all"
                                            : "retainer"
                                    )
                                }
                            >
                                RETAINER
                            </Button>

                            <DatePicker.RangePicker
                                placeholder={["Start date", "End date"]}
                                style={{ width: 240 }}
                                value={dateRange}
                                onChange={setDateRange}
                                format="YYYY-MM-DD"
                            />

                            <Input
                                className="search-input"
                                placeholder="Search..."
                                prefix={
                                    <SearchOutlined
                                        style={{ color: "rgba(0,0,0,.25)" }}
                                    />
                                }
                                suffix={
                                    searchText ? (
                                        <CloseCircleOutlined
                                            style={{
                                                color: "rgba(0,0,0,.25)",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => setSearchText("")}
                                        />
                                    ) : null
                                }
                                style={{ width: 275 }}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onPressEnter={(e) =>
                                    setSearchText(e.target.value)
                                }
                            />
                        </Space>
                    </div>
                </div>

                <div
                    className="projects-filters-row"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div className="filters-left">
                        {showAddButton && (
                            <Button
                                className="btn-white-add"
                                icon={<PlusOutlined />}
                                onClick={onAddProject}
                            >
                                ADD PROJECT
                            </Button>
                        )}
                    </div>

                    <div className="filters-right">
                        <Space size="small">
                            <FilterOutlined
                                style={{
                                    color: departmentFilter.length > 0 || statusFilter.length > 0 ||
                                           projectTypeFilter.length > 0 || industryFilter.length > 0
                                           ? "#FED230" : "#999",
                                    marginRight: 8,
                                    cursor: "pointer",
                                    fontSize: 16
                                }}
                                onClick={() => {
                                    // Reset all filters
                                    setDepartmentFilter([]);
                                    setProjectTypeFilter([]);
                                    setIndustryFilter([]);
                                    // Reset status filter based on active tab
                                    if (activeTab === "confirmed") {
                                        setStatusFilter(["On-Going", "In-Planning"]);
                                    } else if (activeTab === "lead") {
                                        setStatusFilter(["1", "2", "3", "4", "5", "6", "7", "8", "9"]);
                                    } else if (activeTab === "internal") {
                                        setStatusFilter([
                                            "Active",
                                            "On-Going",
                                            "In-Planning",
                                            "Completed",
                                            "Paused",
                                            "Cancelled",
                                            "Finished",
                                            "Archived",
                                        ]);
                                    } else {
                                        setStatusFilter([]);
                                    }
                                }}
                                title="Click to reset all filters"
                            />

                            <Select
                                mode="multiple"
                                placeholder="ALL DEPARTMENTS"
                                className="yellow-dropdown"
                                style={{ minWidth: 200 }}
                                onChange={(values) => {
                                    // Handle clear button click
                                    if (!values || values.length === 0) {
                                        setDepartmentFilter([]);
                                        return;
                                    }

                                    // Filter out SELECT_ALL from the values
                                    const filteredValues = values.filter(v => v !== 'SELECT_ALL');

                                    // Check if "SELECT_ALL" was just clicked
                                    const allDepartmentsClicked = values.includes('SELECT_ALL') && !departmentFilter.includes('SELECT_ALL');

                                    if (allDepartmentsClicked) {
                                        // Toggle all: if all are selected, clear all; otherwise select all
                                        if (departmentFilter.length === departments.length) {
                                            setDepartmentFilter([]);
                                        } else {
                                            setDepartmentFilter(departments.map(d => d.id));
                                        }
                                    } else {
                                        // Check if all were previously selected and user clicked on one
                                        if (departmentFilter.length === departments.length) {
                                            // All were selected, user clicked one to deselect
                                            if (filteredValues.length < departmentFilter.length) {
                                                // Find what was deselected - keep only that item
                                                const deselectedItem = departmentFilter.find(id => !filteredValues.includes(id));
                                                if (deselectedItem) {
                                                    // Keep only the deselected item (inverse behavior)
                                                    setDepartmentFilter([deselectedItem]);
                                                } else {
                                                    setDepartmentFilter(filteredValues);
                                                }
                                            } else {
                                                // User selected something new
                                                setDepartmentFilter(filteredValues);
                                            }
                                        } else {
                                            // Not all were selected, normal behavior
                                            setDepartmentFilter(filteredValues);
                                        }
                                    }
                                }}
                                value={departmentFilter}
                                allowClear
                                maxTagCount={0}
                                maxTagPlaceholder={(omittedValues) => {
                                    if (departmentFilter.length === departments.length) {
                                        return "ALL DEPARTMENTS";
                                    }
                                    return omittedValues.length === 0
                                        ? "ALL DEPARTMENTS"
                                        : `${omittedValues.length} SELECTED`;
                                }}
                                dropdownStyle={{
                                    maxHeight: "none",
                                    minWidth: 300,
                                }}
                            >
                                <Option
                                    key="SELECT_ALL"
                                    value="SELECT_ALL"
                                    style={{
                                        fontWeight: 'bold',
                                        borderBottom: '1px solid #f0f0f0',
                                        marginBottom: 4
                                    }}
                                >
                                    {departmentFilter.length === departments.length
                                        ? '✓ All Departments'
                                        : 'Select All Departments'}
                                </Option>
                                {departments.map((department) => (
                                    <Option
                                        key={department.id}
                                        value={department.id}
                                    >
                                        {department.name
                                            .charAt(0)
                                            .toUpperCase() +
                                            department.name
                                                .slice(1)
                                                .toLowerCase()}
                                    </Option>
                                ))}
                            </Select>

                            {/* Only show status filter if NOT on prospect tab */}
                            {activeTab !== "prospect" && (
                                <Select
                                    mode="multiple"
                                    placeholder="All Status"
                                    className="yellow-dropdown"
                                    style={{ minWidth: 200 }}
                                    onChange={(values) => {
                                        // Handle clear button click
                                        if (!values || values.length === 0) {
                                            setStatusFilter([]);
                                            return;
                                        }

                                        // Filter out ALL_STATUS_TOGGLE from the values
                                        const filteredValues = values.filter(v => v !== 'ALL_STATUS_TOGGLE');

                                        // Check if an issue option was just added
                                        const hasNewDateIssue = filteredValues.includes('DateIssue') && !statusFilter.includes('DateIssue');
                                        const hasNewFinanceIssue = filteredValues.includes('FinanceIssue') && !statusFilter.includes('FinanceIssue');
                                        const issueOptionClicked = hasNewDateIssue || hasNewFinanceIssue;

                                        // Check if a regular status was just added when an issue filter is active
                                        const hasIssueFilter = statusFilter.includes('DateIssue') || statusFilter.includes('FinanceIssue');
                                        const regularStatuses = activeTab === "lead"
                                            ? ['1', '2', '3', '4', '5', '6', '7', '8', '9']
                                            : ['In-Planning', 'On-Going', 'Paused', 'Canceled', 'Finished', 'Archived'];
                                        const newRegularStatus = filteredValues.find(v => regularStatuses.includes(v) && !statusFilter.includes(v));

                                        // Check if "ALL_STATUS_TOGGLE" was just added (clicked)
                                        const allStatusClicked = values.includes('ALL_STATUS_TOGGLE') && !statusFilter.includes('ALL_STATUS_TOGGLE');

                                        if (issueOptionClicked) {
                                            // When an issue option is clicked, select all regular statuses plus the issue filter
                                            const clickedIssue = hasNewDateIssue ? 'DateIssue' : 'FinanceIssue';
                                            if (activeTab === "lead") {
                                                const regularLeadStatuses = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
                                                setStatusFilter([...regularLeadStatuses, clickedIssue]);
                                            } else {
                                                const regularStatuses = ['In-Planning', 'On-Going', 'Paused', 'Canceled', 'Finished', 'Archived'];
                                                setStatusFilter([...regularStatuses, clickedIssue]);
                                            }
                                        } else if (hasIssueFilter && newRegularStatus) {
                                            // When a regular status is clicked while an issue filter is active, clear issue filter
                                            const regularStatusesInSelection = filteredValues.filter(v => regularStatuses.includes(v));
                                            setStatusFilter(regularStatusesInSelection);
                                        } else if (allStatusClicked) {
                                            if (activeTab === "lead") {
                                                const regularLeadStatuses = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
                                                const currentRegularStatuses = statusFilter.filter(s => regularLeadStatuses.includes(s));
                                                const specialStatuses = statusFilter.filter(s => ['FinanceIssue', 'DateIssue'].includes(s));

                                                if (currentRegularStatuses.length === regularLeadStatuses.length) {
                                                    // All regular statuses are selected, so clear them
                                                    setStatusFilter(specialStatuses);
                                                } else {
                                                    // Not all are selected, so select all regular statuses
                                                    setStatusFilter([...regularLeadStatuses, ...specialStatuses]);
                                                }
                                            } else {
                                                const regularStatuses = ['In-Planning', 'On-Going', 'Paused', 'Canceled', 'Finished', 'Archived'];
                                                const currentRegularStatuses = statusFilter.filter(s => regularStatuses.includes(s));
                                                const specialStatuses = statusFilter.filter(s => ['FinanceIssue', 'DateIssue'].includes(s));

                                                if (currentRegularStatuses.length === regularStatuses.length) {
                                                    // All regular statuses are selected, so clear them
                                                    setStatusFilter(specialStatuses);
                                                } else {
                                                    // Not all are selected, so select all regular statuses
                                                    setStatusFilter([...regularStatuses, ...specialStatuses]);
                                                }
                                            }
                                        } else {
                                            // Check if all regular statuses were previously selected
                                            if (activeTab === "lead") {
                                                const regularLeadStatuses = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
                                                const previousRegularStatuses = statusFilter.filter(s => regularLeadStatuses.includes(s));
                                                const specialStatuses = statusFilter.filter(s => ['FinanceIssue', 'DateIssue'].includes(s));

                                                // If all were selected and user clicked on one
                                                if (previousRegularStatuses.length === regularLeadStatuses.length) {
                                                    // Find what was clicked - if we have fewer items now, keep only what remains in regular statuses
                                                    if (filteredValues.length < statusFilter.length) {
                                                        // User deselected something - in this case when all selected, we want to keep only the deselected item
                                                        const deselectedItem = statusFilter.find(s => !filteredValues.includes(s) && regularLeadStatuses.includes(s));
                                                        if (deselectedItem) {
                                                            // Keep only the deselected item (inverse behavior)
                                                            setStatusFilter([deselectedItem, ...specialStatuses]);
                                                        } else {
                                                            setStatusFilter(filteredValues);
                                                        }
                                                    } else {
                                                        // User selected something new
                                                        setStatusFilter(filteredValues);
                                                    }
                                                } else {
                                                    // Not all were selected, normal behavior
                                                    setStatusFilter(filteredValues);
                                                }
                                            } else {
                                                const regularStatuses = ['In-Planning', 'On-Going', 'Paused', 'Canceled', 'Finished', 'Archived'];
                                                const previousRegularStatuses = statusFilter.filter(s => regularStatuses.includes(s));
                                                const specialStatuses = statusFilter.filter(s => ['FinanceIssue', 'DateIssue'].includes(s));

                                                // If all were selected and user clicked on one
                                                if (previousRegularStatuses.length === regularStatuses.length) {
                                                    // Find what was clicked
                                                    if (filteredValues.length < statusFilter.length) {
                                                        // User deselected something - keep only that item
                                                        const deselectedItem = statusFilter.find(s => !filteredValues.includes(s) && regularStatuses.includes(s));
                                                        if (deselectedItem) {
                                                            // Keep only the deselected item
                                                            setStatusFilter([deselectedItem, ...specialStatuses]);
                                                        } else {
                                                            setStatusFilter(filteredValues);
                                                        }
                                                    } else {
                                                        // User selected something new
                                                        setStatusFilter(filteredValues);
                                                    }
                                                } else {
                                                    // Not all were selected, normal behavior
                                                    setStatusFilter(filteredValues);
                                                }
                                            }
                                        }
                                    }}
                                    value={statusFilter}
                                    allowClear
                                    maxTagCount={0}
                                    maxTagPlaceholder={(omittedValues) => {
                                        // Check if all regular statuses are selected
                                        if (activeTab === "lead") {
                                            const regularLeadStatuses = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
                                            const regularSelected = statusFilter.filter(s => regularLeadStatuses.includes(s));
                                            if (regularSelected.length === regularLeadStatuses.length) {
                                                return "ALL STATUS";
                                            }
                                        } else if (activeTab === "confirmed") {
                                            const regularStatuses = ['In-Planning', 'On-Going', 'Paused', 'Canceled', 'Finished', 'Archived'];
                                            const regularSelected = statusFilter.filter(s => regularStatuses.includes(s));
                                            if (regularSelected.length === regularStatuses.length) {
                                                return "ALL STATUS";
                                            }
                                        }
                                        return omittedValues.length === 0
                                            ? "ALL STATUS"
                                            : `${omittedValues.length} SELECTED`;
                                    }}
                                    dropdownStyle={{
                                        maxHeight: "none",
                                        minWidth: 350,
                                    }}
                                >
                                    {activeTab === "lead" ? (
                                        <>
                                            <Option
                                                value="ALL_STATUS_TOGGLE"
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                All Status
                                            </Option>
                                            <Option value="1">1 - Lead (Qualified)</Option>
                                            <Option value="2">
                                                2 - First discussion
                                            </Option>
                                            <Option value="3">
                                                3 - Brief received
                                            </Option>
                                            <Option value="4">
                                                4 - Work on proposal or quote
                                            </Option>
                                            <Option value="5">
                                                5 - Proposal sent → to follow up
                                            </Option>
                                            <Option value="6">
                                                6 - Verbally Accepted
                                            </Option>
                                            <Option value="7">
                                                7 - Work on Contract
                                            </Option>
                                            <Option value="8">
                                                8 - Won (Contract or Quote signed)
                                            </Option>
                                            <Option value="9">9 - Lost</Option>
                                            {roleService.hasRole(ROLES.ROLE_FINANCE) && (
                                                <>
                                                    <Option value="__SEPARATOR__" disabled style={{ cursor: 'default', padding: '0', minHeight: '1px', lineHeight: '1px', height: 'auto', margin: '4px 12px', background: '#e8e8e8', pointerEvents: 'none', fontSize: '1px' }}>
                                                        &nbsp;
                                                    </Option>
                                                    <Option value="DateIssue">
                                                        Had Date Issue
                                                    </Option>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Option
                                                value="ALL_STATUS_TOGGLE"
                                                style={{ fontWeight: 'bold' }}
                                            >
                                                All Status
                                            </Option>
                                            <Option value="In-Planning">
                                                In-Planning
                                            </Option>
                                            <Option value="On-Going">
                                                On-Going
                                            </Option>
                                            <Option value="Paused">
                                                Paused
                                            </Option>
                                            <Option value="Canceled">
                                                Canceled
                                            </Option>
                                            <Option value="Finished">
                                                Finished
                                            </Option>
                                            <Option value="Archived">
                                                Archived
                                            </Option>
                                            {roleService.hasRole(ROLES.ROLE_FINANCE) && (
                                                <>
                                                    <Option value="__SEPARATOR__" disabled style={{ cursor: 'default', padding: '0', minHeight: '1px', lineHeight: '1px', height: 'auto', margin: '4px 12px', background: '#e8e8e8', pointerEvents: 'none', fontSize: '1px' }}>
                                                        &nbsp;
                                                    </Option>
                                                    <Option value="FinanceIssue">
                                                        Had Finance Issue
                                                    </Option>
                                                    <Option value="DateIssue">
                                                        Had Date Issue
                                                    </Option>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Select>
                            )}

                            <Select
                                mode="multiple"
                                placeholder="ALL PROJECT TYPE"
                                className="yellow-dropdown"
                                style={{ minWidth: 200 }}
                                onChange={(values) => {
                                    // Handle clear button click
                                    if (!values || values.length === 0) {
                                        setProjectTypeFilter([]);
                                        return;
                                    }

                                    // Filter out SELECT_ALL from the values
                                    const filteredValues = values.filter(v => v !== 'SELECT_ALL');

                                    // Check if "SELECT_ALL" was just clicked
                                    const allTypesClicked = values.includes('SELECT_ALL') && !projectTypeFilter.includes('SELECT_ALL');

                                    if (allTypesClicked) {
                                        // Toggle all: if all are selected, clear all; otherwise select all
                                        if (projectTypeFilter.length === projectClassifications.length) {
                                            setProjectTypeFilter([]);
                                        } else {
                                            setProjectTypeFilter(projectClassifications.map(p => p.id));
                                        }
                                    } else {
                                        // Check if all were previously selected and user clicked on one
                                        if (projectTypeFilter.length === projectClassifications.length) {
                                            // All were selected, user clicked one to deselect
                                            if (filteredValues.length < projectTypeFilter.length) {
                                                // Find what was deselected - keep only that item
                                                const deselectedItem = projectTypeFilter.find(id => !filteredValues.includes(id));
                                                if (deselectedItem) {
                                                    // Keep only the deselected item (inverse behavior)
                                                    setProjectTypeFilter([deselectedItem]);
                                                } else {
                                                    setProjectTypeFilter(filteredValues);
                                                }
                                            } else {
                                                // User selected something new
                                                setProjectTypeFilter(filteredValues);
                                            }
                                        } else {
                                            // Not all were selected, normal behavior
                                            setProjectTypeFilter(filteredValues);
                                        }
                                    }
                                }}
                                value={projectTypeFilter}
                                allowClear
                                maxTagCount={0}
                                maxTagPlaceholder={(omittedValues) => {
                                    if (projectTypeFilter.length === projectClassifications.length) {
                                        return "ALL PROJECT TYPE";
                                    }
                                    return omittedValues.length === 0
                                        ? "ALL PROJECT TYPE"
                                        : `${omittedValues.length} SELECTED`;
                                }}
                                dropdownStyle={{
                                    maxHeight: "none",
                                    minWidth: 350,
                                }}
                            >
                                <Option
                                    key="SELECT_ALL"
                                    value="SELECT_ALL"
                                    style={{
                                        fontWeight: 'bold',
                                        borderBottom: '1px solid #f0f0f0',
                                        marginBottom: 4
                                    }}
                                >
                                    {projectTypeFilter.length === projectClassifications.length
                                        ? '✓ All Project Types'
                                        : 'Select All Project Types'}
                                </Option>
                                {projectClassifications.map(
                                    (classification) => (
                                        <Option
                                            key={classification.id}
                                            value={classification.id}
                                        >
                                            {classification.name
                                                .charAt(0)
                                                .toUpperCase() +
                                                classification.name
                                                    .slice(1)
                                                    .toLowerCase()}
                                        </Option>
                                    )
                                )}
                            </Select>

                            <Select
                                mode="multiple"
                                placeholder="ALL PROJECT INDUSTRY"
                                className="yellow-dropdown"
                                style={{ minWidth: 220 }}
                                onChange={(values) => {
                                    // Handle clear button click
                                    if (!values || values.length === 0) {
                                        setIndustryFilter([]);
                                        return;
                                    }

                                    // Filter out SELECT_ALL from the values
                                    const filteredValues = values.filter(v => v !== 'SELECT_ALL');

                                    // Check if "SELECT_ALL" was just clicked
                                    const allIndustriesClicked = values.includes('SELECT_ALL') && !industryFilter.includes('SELECT_ALL');

                                    if (allIndustriesClicked) {
                                        // Toggle all: if all are selected, clear all; otherwise select all
                                        if (industryFilter.length === industryClassifications.length) {
                                            setIndustryFilter([]);
                                        } else {
                                            setIndustryFilter(industryClassifications.map(i => i.id));
                                        }
                                    } else {
                                        // Check if all were previously selected and user clicked on one
                                        if (industryFilter.length === industryClassifications.length) {
                                            // All were selected, user clicked one to deselect
                                            if (filteredValues.length < industryFilter.length) {
                                                // Find what was deselected - keep only that item
                                                const deselectedItem = industryFilter.find(id => !filteredValues.includes(id));
                                                if (deselectedItem) {
                                                    // Keep only the deselected item (inverse behavior)
                                                    setIndustryFilter([deselectedItem]);
                                                } else {
                                                    setIndustryFilter(filteredValues);
                                                }
                                            } else {
                                                // User selected something new
                                                setIndustryFilter(filteredValues);
                                            }
                                        } else {
                                            // Not all were selected, normal behavior
                                            setIndustryFilter(filteredValues);
                                        }
                                    }
                                }}
                                value={industryFilter}
                                allowClear
                                maxTagCount={0}
                                maxTagPlaceholder={(omittedValues) => {
                                    if (industryFilter.length === industryClassifications.length) {
                                        return "ALL PROJECT INDUSTRY";
                                    }
                                    return omittedValues.length === 0
                                        ? "ALL PROJECT INDUSTRY"
                                        : `${omittedValues.length} SELECTED`;
                                }}
                                dropdownStyle={{
                                    maxHeight: "none",
                                    minWidth: 400,
                                }}
                            >
                                <Option
                                    key="SELECT_ALL"
                                    value="SELECT_ALL"
                                    style={{
                                        fontWeight: 'bold',
                                        borderBottom: '1px solid #f0f0f0',
                                        marginBottom: 4
                                    }}
                                >
                                    {industryFilter.length === industryClassifications.length
                                        ? '✓ All Industries'
                                        : 'Select All Industries'}
                                </Option>
                                {industryClassifications.map((industry) => (
                                    <Option
                                        key={industry.id}
                                        value={industry.id}
                                    >
                                        {industry.name.charAt(0).toUpperCase() +
                                            industry.name
                                                .slice(1)
                                                .toLowerCase()}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </div>
                </div>
            </div>

            <div
                className="tabs-and-results-section"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    // marginBottom: 16,
                }}
            >
                <div className="tabs-section">
                    <Tabs
                        activeKey={activeTab}
                        onChange={onTabChange}
                        items={tabItems.map((item) => ({
                            ...item,
                            label: (
                                <span
                                    style={{
                                        fontWeight:
                                            item.key === activeTab ? 600 : 400,
                                        color:
                                            item.key === activeTab
                                                ? "#1b1b1b"
                                                : "#8c8c8c",
                                    }}
                                >
                                    {item.label}
                                </span>
                            ),
                        }))}
                        type="card"
                    />
                </div>
                {/* Hide results count on Issues tab */}
                {activeTab !== "issues" && (
                    <div
                        className="results-count"
                        style={{ fontSize: 13, color: "#666" }}
                    >
                        Found {pagination ? pagination.total : filteredProjects.length} Results with filter Type,
                        Status
                    </div>
                )}
            </div>

            {/* Conditional rendering: Issues view or regular table */}
            {activeTab === "issues" ? (
                // Issues Tab View
                <div className="issues-view" style={{ marginTop: 24 }}>
                    {/* Issue category tabs */}
                    <Tabs
                        activeKey={activeIssuesTab}
                        onChange={(key) => setActiveIssuesTab(key)}
                        size="large"
                        style={{ marginBottom: 16 }}
                        items={Object.entries(CATEGORY_GROUPS).map(([key, group]) => ({
                            key,
                            label: group.label,
                        }))}
                    />

                    {/* Date Filter Panel */}
                    <Card
                        title="Filter by Project Start Date"
                        style={{ marginBottom: 16 }}
                        size="small"
                    >
                        <Space direction="horizontal" size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
                            <Select
                                value={dateFilterType}
                                onChange={(value) => {
                                    setDateFilterType(value);
                                    if (value !== 'custom') {
                                        setCustomDateRange([null, null]);
                                    }
                                }}
                                style={{ width: 200 }}
                                options={[
                                    { label: 'All Projects', value: 'all' },
                                    { label: 'This Month', value: 'thisMonth' },
                                    { label: 'Last Month', value: 'lastMonth' },
                                    { label: 'Last 3 Months', value: 'last3Months' },
                                    { label: 'This Financial Year', value: 'thisFY' },
                                    { label: 'Last Financial Year', value: 'lastFY' },
                                    { label: 'Custom Range', value: 'custom' },
                                ]}
                            />
                            {dateFilterType === 'custom' && (
                                <RangePicker
                                    value={customDateRange}
                                    onChange={(dates) => setCustomDateRange(dates)}
                                    format="YYYY-MM-DD"
                                    style={{ width: 300 }}
                                />
                            )}
                            {dateFilterType !== 'all' && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {(() => {
                                        const range = getDateRange();
                                        if (range) {
                                            return `Filtering projects from ${range.startDate} to ${range.endDate}`;
                                        }
                                        return 'Select a date range';
                                    })()}
                                </Typography.Text>
                            )}
                        </Space>
                    </Card>

                    {/* Progress Panel - shown during auto-scan */}
                    {isScanning && (
                        <Card
                            style={{ marginBottom: 24 }}
                            size="small"
                        >
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <div style={{ textAlign: 'center' }}>
                                    <Spin size="large" />
                                    <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
                                        Scanning {CATEGORY_GROUPS[activeIssuesTab].label}...
                                    </Typography.Title>
                                    <Typography.Text type="secondary">
                                        Analyzing {CATEGORY_GROUPS[activeIssuesTab].categories.length} issue categories
                                    </Typography.Text>
                                </div>

                                {/* Real-time progress bar */}
                                {progress.total > 0 && (
                                    <>
                                        <Progress
                                            percent={Math.round((progress.current / progress.total) * 100)}
                                            status="active"
                                            strokeColor={{ from: '#108ee9', to: '#87d068' }}
                                        />
                                        <Typography.Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                                            Processing {progress.current} of {progress.total} projects
                                        </Typography.Text>
                                    </>
                                )}

                                {/* Show indeterminate progress while loading project list */}
                                {progress.total === 0 && (
                                    <Progress percent={100} status="active" showInfo={false} />
                                )}
                            </Space>
                        </Card>
                    )}

                    {/* Results Section - only show after scan has been performed AND not currently scanning */}
                    {hasScanned && !isScanning && (
                        <>
                            {issuesLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" tip="Loading issues..." />
                                </div>
                            ) : issuesData?.hasErrors ? (
                                <Empty
                                    image={<ExclamationCircleOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />}
                                    description={
                                        <div>
                                            <Typography.Title level={4} style={{ color: '#ff4d4f' }}>Scan Failed</Typography.Title>
                                            <Typography.Text type="danger">
                                                {issuesData.globalError || `${issuesData.errorCount || 0} categor${issuesData.errorCount !== 1 ? 'ies' : 'y'} failed to scan.`}
                                            </Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">
                                                Check the error details above or try scanning again.
                                            </Typography.Text>
                                        </div>
                                    }
                                />
                            ) : !issuesData || !issuesData.issueGroups || issuesData.issueGroups.length === 0 ? (
                                <Empty
                                    image={<CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />}
                                    description={
                                        <div>
                                            <Typography.Title level={4}>No Issues Found!</Typography.Title>
                                            <Typography.Text type="secondary">
                                                All your projects are in good shape.
                                            </Typography.Text>
                                        </div>
                                    }
                                />
                            ) : (
                        <>
                            {/* Issues Summary Card */}
                            <Card style={{ marginBottom: 24 }}>
                                <Row gutter={[16, 16]}>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text strong>Total Projects Checked:</Typography.Text>
                                        <Typography.Title level={3} style={{ margin: 0 }}>
                                            {issuesData.totalProjectsChecked || 0}
                                        </Typography.Title>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text strong>Total Issues Found:</Typography.Text>
                                        <Typography.Title level={3} style={{ margin: 0, color: '#ff4d4f' }}>
                                            {issuesData.totalIssuesFound || 0}
                                        </Typography.Title>
                                    </div>
                                </Row>
                            </Card>

                            {/* Issue Groups as Collapsible Panels */}
                            <style>{`
                                .issues-collapse .ant-collapse-content-box {
                                    padding: 16px !important;
                                }
                                .issues-collapse .ant-collapse-header {
                                    padding-top: 8px !important;
                                    padding-bottom: 8px !important;
                                }
                            `}</style>
                            <Collapse
                                defaultActiveKey={CATEGORY_GROUPS[activeIssuesTab].categories.filter(code => {
                                    const group = issuesData.issueGroups.find(g => g.category === code);
                                    return group && group.count > 0;
                                })}
                                style={{ marginBottom: 24 }}
                                className="issues-collapse"
                                items={CATEGORY_GROUPS[activeIssuesTab].categories.map((categoryCode) => {
                                    // Find the actual result for this category, or create an empty one
                                    const group = issuesData.issueGroups.find(g => g.category === categoryCode) || {
                                        category: categoryCode,
                                        count: 0,
                                        projects: []
                                    };
                                    // Determine icon and color based on category
                                    let icon, color;
                                    if (group.category.includes('DATE')) {
                                        icon = <WarningOutlined />;
                                        color = '#faad14'; // Orange
                                    } else if (group.category === 'VENDOR_REVENUE_HIGH') {
                                        icon = <InfoCircleOutlined />;
                                        color = '#52c41a'; // Green (good problem)
                                    } else if (group.category === 'VENDOR_REVENUE_PLANNED_COST_DISCREPANCY') {
                                        icon = <WarningOutlined />;
                                        color = '#faad14'; // Orange
                                    } else if (group.category === 'INVOICE_ALLOCATION_VARIANCE') {
                                        icon = <ExclamationCircleOutlined />;
                                        color = '#ff9800'; // Orange for variance
                                    } else {
                                        icon = <ExclamationCircleOutlined />;
                                        color = '#ff4d4f'; // Red
                                    }

                                    // Inline edit handler for issue scanner projects
                                    const handleInlineProjectUpdate = async (projectId, updateData) => {
                                        const response = await nodeAPI.put(`/project/${projectId}`, updateData);
                                        if (response.data?.statusCode !== 1 && response.status !== 200) {
                                            throw new Error('Failed to update project');
                                        }

                                        setIssuesData(prev => {
                                            if (!prev) return prev;
                                            const newData = { ...prev };
                                            newData.issueGroups = newData.issueGroups.map(g => {
                                                if (g.category !== 'PROJECTS_PAST_END_DATE') return g;

                                                const shouldRemove =
                                                    (updateData.status && ['Finished', 'Archived', 'Canceled'].includes(updateData.status))
                                                    || (updateData.endDate && new Date(updateData.endDate) >= new Date(new Date().toDateString()));

                                                if (shouldRemove) {
                                                    const updatedGroup = { ...g };
                                                    updatedGroup.projects = g.projects.filter(p => p.project_id !== projectId);
                                                    updatedGroup.count = updatedGroup.projects.length;
                                                    if (updatedGroup.groupedByOwner) {
                                                        updatedGroup.groupedByOwner = updatedGroup.groupedByOwner.map(og => ({
                                                            ...og,
                                                            projects: og.projects.filter(p => p.project_id !== projectId),
                                                            count: og.projects.filter(p => p.project_id !== projectId).length,
                                                        })).filter(og => og.projects.length > 0);
                                                    }
                                                    return updatedGroup;
                                                }

                                                // Update in place (status changed but still overdue, or end date still in past)
                                                const updateProject = (p) => {
                                                    if (p.project_id !== projectId) return p;
                                                    const updated = { ...p };
                                                    if (updateData.status) updated.status = updateData.status;
                                                    if (updateData.endDate) {
                                                        updated.end_date = updateData.endDate;
                                                        const endDate = new Date(updateData.endDate);
                                                        endDate.setHours(0, 0, 0, 0);
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        updated.daysOverdue = Math.floor((today - endDate) / (1000 * 60 * 60 * 24));
                                                    }
                                                    return updated;
                                                };
                                                const updatedGroup = { ...g };
                                                updatedGroup.projects = g.projects.map(updateProject);
                                                if (updatedGroup.groupedByOwner) {
                                                    updatedGroup.groupedByOwner = updatedGroup.groupedByOwner.map(og => ({
                                                        ...og,
                                                        projects: og.projects.map(updateProject),
                                                    }));
                                                }
                                                return updatedGroup;
                                            });
                                            return newData;
                                        });

                                        message.success('Project updated successfully');
                                    };

                                    // Column definitions per category
                                    const getColumns = (category) => {
                                        const baseColumns = [
                                            {
                                                title: 'Project ID',
                                                dataIndex: 'generated_id',
                                                key: 'generated_id',
                                                width: 120,
                                                sorter: (a, b) => (a.generated_id || '').localeCompare(b.generated_id || ''),
                                                render: (text, record) => (
                                                    <Link to={`/project-management/project/${record.project_id}`} target="_blank" rel="noopener noreferrer">
                                                        {text}
                                                    </Link>
                                                ),
                                            },
                                            {
                                                title: 'Project Name',
                                                dataIndex: 'name',
                                                key: 'name',
                                                width: 250,
                                                sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                                            },
                                        ];

                                        const issuePicColumn = (width = 150) => ({
                                            title: 'PIC',
                                            dataIndex: 'pic_name',
                                            key: 'pic_name',
                                            width,
                                            sorter: (a, b) => (a.pic_name || '').localeCompare(b.pic_name || ''),
                                            render: renderIssuePicName,
                                        });

                                        const appendIssueRemarkColumn = (columns) => {
                                            if (!ISSUE_REMARK_CATEGORIES.has(category)) {
                                                return columns;
                                            }

                                            return [
                                                ...columns,
                                                {
                                                    title: 'Remark',
                                                    dataIndex: 'issueRemark',
                                                    key: 'issueRemark',
                                                    width: 180,
                                                    className: 'project-issue-remark-cell',
                                                    render: (issueRemark, record) => {
                                                        const text = issueRemark?.remark?.trim();
                                                        const canEdit = canEditIssueRemarks();
                                                        const lastEditedBy = issueRemark?.updatedByName || issueRemark?.createdByName || 'Unknown';
                                                        const lastEditedAt = formatIssueRemarkTimestamp(issueRemark?.updatedAt || issueRemark?.createdAt);
                                                        const preview = (
                                                            <div
                                                                className="project-issue-remark-content"
                                                                style={{
                                                                    color: text ? 'inherit' : 'rgba(0, 0, 0, 0.45)',
                                                                    cursor: text ? 'help' : 'default',
                                                                }}
                                                                onClick={(event) => {
                                                                    if (text) {
                                                                        event.stopPropagation();
                                                                    }
                                                                }}
                                                            >
                                                                <span
                                                                    className={`project-issue-remark-preview${text ? '' : ' is-empty'}`}
                                                                >
                                                                    {text || '-'}
                                                                </span>
                                                            </div>
                                                        );

                                                        return (
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                                                                {text ? (
                                                                    <Popover
                                                                        placement="topLeft"
                                                                        trigger={['hover', 'click']}
                                                                        overlayStyle={{ maxWidth: 640 }}
                                                                        content={(
                                                                            <div
                                                                                style={{
                                                                                    maxWidth: 600,
                                                                                    maxHeight: 320,
                                                                                    overflowY: 'auto',
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    wordBreak: 'break-word',
                                                                                    overflowWrap: 'anywhere',
                                                                                    lineHeight: 1.5,
                                                                                }}
                                                                            >
                                                                                <div className="project-issue-remark-popover-meta">
                                                                                    <div>
                                                                                        <Typography.Text type="secondary">Last updated by</Typography.Text>
                                                                                        <div><Typography.Text strong>{lastEditedBy}</Typography.Text></div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <Typography.Text type="secondary">Last updated at</Typography.Text>
                                                                                        <div><Typography.Text>{lastEditedAt}</Typography.Text></div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="project-issue-remark-popover-text">
                                                                                    {renderRemarkTextWithLinks(text)}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    >
                                                                        {preview}
                                                                    </Popover>
                                                                ) : preview}
                                                                {canEdit && (
                                                                    <Tooltip title={text ? 'Edit remark' : 'Add remark'}>
                                                                        <button
                                                                            type="button"
                                                                            aria-label={text ? 'Edit remark' : 'Add remark'}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                openIssueRemarkEditor(record, category);
                                                                            }}
                                                                            style={{
                                                                                width: 28,
                                                                                height: 28,
                                                                                border: '1px solid #d9d9d9',
                                                                                borderRadius: 4,
                                                                                background: '#fff',
                                                                                color: '#1b1b1b',
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                cursor: 'pointer',
                                                                                flex: '0 0 auto'
                                                                            }}
                                                                        >
                                                                            <EditOutlined />
                                                                        </button>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        );
                                                    },
                                                }
                                            ];
                                        };

                                        if (category === 'DATE_PRE_START_HOURS') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 140,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date, record) => {
                                                        if (!date) return '-';
                                                        const formattedDate = new Date(date).toLocaleDateString();
                                                        // Show warning icon for future projects
                                                        if (record.is_future_project) {
                                                            return (
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <WarningOutlined key="icon" style={{ color: '#faad14', fontSize: '14px' }} />
                                                                    <span key="date">{formattedDate}</span>
                                                                </span>
                                                            );
                                                        }
                                                        return formattedDate;
                                                    },
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 140,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status) => {
                                                        if (status === 'In-Planning') {
                                                            return (
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <WarningOutlined key="icon" style={{ color: '#faad14', fontSize: '14px' }} />
                                                                    <span key="status">{status}</span>
                                                                </span>
                                                            );
                                                        }
                                                        return status || '-';
                                                    },
                                                },
                                                {
                                                    title: 'Hours Logged',
                                                    dataIndex: 'hours_logged',
                                                    key: 'hours_logged',
                                                    width: 120,
                                                    sorter: (a, b) => (a.hours_logged || 0) - (b.hours_logged || 0),
                                                    render: (hours) => `${hours || 0} hrs`,
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_REVENUE_LOW') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Vendor Revenue (SGD)',
                                                    dataIndex: 'vendorRevenueSgd',
                                                    key: 'vendorRevenueSgd',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorRevenueSgd || 0) - (b.vendorRevenueSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Vendor Planned Cost (SGD)',
                                                    dataIndex: 'vendorPlannedCostSgd',
                                                    key: 'vendorPlannedCostSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.vendorPlannedCostSgd || 0) - (b.vendorPlannedCostSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Shortfall (SGD)',
                                                    dataIndex: 'shortfallSgd',
                                                    key: 'shortfallSgd',
                                                    width: 120,
                                                    sorter: (a, b) => (a.shortfallSgd || 0) - (b.shortfallSgd || 0),
                                                    render: (val) => <span style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(val)}</span>,
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_REVENUE_HIGH') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Vendor Revenue (SGD)',
                                                    dataIndex: 'vendorRevenueSgd',
                                                    key: 'vendorRevenueSgd',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorRevenueSgd || 0) - (b.vendorRevenueSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Vendor Planned Cost (SGD)',
                                                    dataIndex: 'vendorPlannedCostSgd',
                                                    key: 'vendorPlannedCostSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.vendorPlannedCostSgd || 0) - (b.vendorPlannedCostSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Surplus (SGD)',
                                                    dataIndex: 'surplusSgd',
                                                    key: 'surplusSgd',
                                                    width: 120,
                                                    sorter: (a, b) => (a.surplusSgd || 0) - (b.surplusSgd || 0),
                                                    render: (val) => <span style={{ color: '#52c41a' }}>+{formatSgdCurrency(val)}</span>,
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_REVENUE_PLANNED_COST_DISCREPANCY') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Vendor Revenue (SGD)',
                                                    dataIndex: 'vendorRevenueSgd',
                                                    key: 'vendorRevenueSgd',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorRevenueSgd || 0) - (b.vendorRevenueSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Vendor Planned Cost (SGD)',
                                                    dataIndex: 'vendorPlannedCostSgd',
                                                    key: 'vendorPlannedCostSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.vendorPlannedCostSgd || 0) - (b.vendorPlannedCostSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Difference (SGD)',
                                                    dataIndex: 'discrepancySgd',
                                                    key: 'discrepancySgd',
                                                    width: 140,
                                                    sorter: (a, b) => (a.discrepancySgd || 0) - (b.discrepancySgd || 0),
                                                    render: (val, record) => {
                                                        const isAbove = record.discrepancyDirection === 'Revenue above planned';
                                                        return (
                                                            <span style={{ color: isAbove ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                                                                {isAbove ? '+' : '-'}{formatSgdCurrency(val)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Direction',
                                                    dataIndex: 'discrepancyDirection',
                                                    key: 'discrepancyDirection',
                                                    width: 170,
                                                    sorter: (a, b) => (a.discrepancyDirection || '').localeCompare(b.discrepancyDirection || ''),
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_BILLS_OVER_PLANNED') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Received Vendor Bills (SGD)',
                                                    dataIndex: 'receivedVendorBillsSgd',
                                                    key: 'receivedVendorBillsSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.receivedVendorBillsSgd || 0) - (b.receivedVendorBillsSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Vendor Planned Cost (SGD)',
                                                    dataIndex: 'vendorPlannedCostSgd',
                                                    key: 'vendorPlannedCostSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.vendorPlannedCostSgd || 0) - (b.vendorPlannedCostSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Over Budget (SGD)',
                                                    dataIndex: 'overBudgetSgd',
                                                    key: 'overBudgetSgd',
                                                    width: 120,
                                                    sorter: (a, b) => (a.overBudgetSgd || 0) - (b.overBudgetSgd || 0),
                                                    render: (val) => <span style={{ color: '#ff4d4f' }}>+{formatSgdCurrency(val)}</span>,
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_BILLS_OVER_REVENUE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Received Vendor Bills (SGD)',
                                                    dataIndex: 'receivedVendorBillsSgd',
                                                    key: 'receivedVendorBillsSgd',
                                                    width: 180,
                                                    sorter: (a, b) => (a.receivedVendorBillsSgd || 0) - (b.receivedVendorBillsSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Vendor Revenue (SGD)',
                                                    dataIndex: 'vendorRevenueSgd',
                                                    key: 'vendorRevenueSgd',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorRevenueSgd || 0) - (b.vendorRevenueSgd || 0),
                                                    render: (val) => formatSgdCurrency(val),
                                                },
                                                {
                                                    title: 'Loss (SGD)',
                                                    dataIndex: 'lossSgd',
                                                    key: 'lossSgd',
                                                    width: 120,
                                                    sorter: (a, b) => (a.lossSgd || 0) - (b.lossSgd || 0),
                                                    render: (val) => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>-{formatSgdCurrency(val)}</span>,
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'INVOICE_ALLOCATION_VARIANCE') {
                                            return appendIssueRemarkColumn([
                                                {
                                                    title: 'Project ID',
                                                    dataIndex: 'generated_id',
                                                    key: 'generated_id',
                                                    width: 120,
                                                    sorter: (a, b) => (a.generated_id || '').localeCompare(b.generated_id || ''),
                                                    render: (text, record) => (
                                                        <Link to={`/project-management/project/${record.project_id}`}>
                                                            {text}
                                                        </Link>
                                                    ),
                                                },
                                                {
                                                    title: 'Project Name',
                                                    dataIndex: 'name',
                                                    key: 'name',
                                                    width: 250,
                                                    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                                                },
                                                {
                                                    title: 'Invoice',
                                                    dataIndex: 'invoice_no',
                                                    key: 'invoice_no',
                                                    width: 150,
                                                    sorter: (a, b) => (a.invoice_no || '').localeCompare(b.invoice_no || ''),
                                                },
                                                {
                                                    title: 'Project Allocation',
                                                    dataIndex: 'project_allocation',
                                                    key: 'project_allocation',
                                                    width: 160,
                                                    sorter: (a, b) => (a.project_allocation || 0) - (b.project_allocation || 0),
                                                    render: (val, record) => `${record.currency || 'USD'} ${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                                },
                                                {
                                                    title: 'Line Item Sum',
                                                    dataIndex: 'line_item_sum',
                                                    key: 'line_item_sum',
                                                    width: 160,
                                                    sorter: (a, b) => (a.line_item_sum || 0) - (b.line_item_sum || 0),
                                                    render: (val, record) => `${record.currency || 'USD'} ${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                                },
                                                {
                                                    title: 'Variance',
                                                    dataIndex: 'variance_amount',
                                                    key: 'variance_amount',
                                                    width: 140,
                                                    sorter: (a, b) => (a.variance_amount || 0) - (b.variance_amount || 0),
                                                    render: (val, record) => {
                                                        // Calculate if line items are higher or lower than project allocation
                                                        const isOverAllocated = record.line_item_sum > record.project_allocation;
                                                        const sign = isOverAllocated ? '+' : '-';
                                                        return (
                                                            <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                                {sign}{(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'NO_CLIENT_INVOICES' || category === 'NO_SALES_ORDERS') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'INVOICES_AFTER_END_DATE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'Invoice Count',
                                                    dataIndex: 'invoice_count',
                                                    key: 'invoice_count',
                                                    width: 120,
                                                    sorter: (a, b) => (a.invoice_count || 0) - (b.invoice_count || 0),
                                                },
                                                {
                                                    title: 'Latest Invoice Date',
                                                    key: 'latest_invoice_date',
                                                    width: 150,
                                                    render: (text, record) => {
                                                        if (!record.invoices_after_end || record.invoices_after_end.length === 0) return '-';
                                                        const latestInvoice = record.invoices_after_end.reduce((latest, inv) => {
                                                            return new Date(inv.invoice_date) > new Date(latest.invoice_date) ? inv : latest;
                                                        });
                                                        return new Date(latestInvoice.invoice_date).toLocaleDateString();
                                                    },
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'BILLS_AFTER_END_DATE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'Bill Count',
                                                    dataIndex: 'bill_count',
                                                    key: 'bill_count',
                                                    width: 120,
                                                    sorter: (a, b) => (a.bill_count || 0) - (b.bill_count || 0),
                                                },
                                                {
                                                    title: 'Latest Bill Date',
                                                    key: 'latest_bill_date',
                                                    width: 150,
                                                    render: (text, record) => {
                                                        if (!record.bills_after_end || record.bills_after_end.length === 0) return '-';
                                                        const latestBill = record.bills_after_end.reduce((latest, bill) => {
                                                            return new Date(bill.bill_date) > new Date(latest.bill_date) ? bill : latest;
                                                        });
                                                        return new Date(latestBill.bill_date).toLocaleDateString();
                                                    },
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'TIME_OUTSIDE_DATE_RANGE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'Time Entry Count',
                                                    dataIndex: 'time_entry_count',
                                                    key: 'time_entry_count',
                                                    width: 120,
                                                    sorter: (a, b) => (a.time_entry_count || 0) - (b.time_entry_count || 0),
                                                },
                                                {
                                                    title: 'Total Hours',
                                                    dataIndex: 'total_hours',
                                                    key: 'total_hours',
                                                    width: 120,
                                                    sorter: (a, b) => (a.total_hours || 0) - (b.total_hours || 0),
                                                    render: (hours) => hours ? `${hours.toFixed(2)}h` : '-',
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'WORK_ACCOMPLISHED_OUTSIDE_RANGE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Start Date',
                                                    dataIndex: 'start_date',
                                                    key: 'start_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
                                                        const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'Invoice Count',
                                                    dataIndex: 'work_accomplished_count',
                                                    key: 'work_accomplished_count',
                                                    width: 120,
                                                    sorter: (a, b) => (a.work_accomplished_count || 0) - (b.work_accomplished_count || 0),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'VENDOR_PLANNING_EMPTY') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 120,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                {
                                                    title: 'Estimated Vendor Cost',
                                                    dataIndex: 'estimatedVendorCost',
                                                    key: 'estimatedVendorCost',
                                                    width: 180,
                                                    sorter: (a, b) => (a.estimatedVendorCost || 0) - (b.estimatedVendorCost || 0),
                                                    render: (val, record) => formatIssueCurrency(val, record),
                                                },
                                                {
                                                    title: 'Total Planned',
                                                    dataIndex: 'totalPlanned',
                                                    key: 'totalPlanned',
                                                    width: 150,
                                                    sorter: (a, b) => (a.totalPlanned || 0) - (b.totalPlanned || 0),
                                                    render: (val, record) => {
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                                                {formatIssueCurrency(val || 0, record)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Planning Gap',
                                                    dataIndex: 'gapProjectCurrency',
                                                    key: 'gapProjectCurrency',
                                                    width: 150,
                                                    sorter: (a, b) => (a.gapProjectCurrency || 0) - (b.gapProjectCurrency || 0),
                                                    render: (val, record) => (
                                                        <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                            -{formatIssueCurrency(val, record)}
                                                        </span>
                                                    ),
                                                },
                                                issuePicColumn(),
                                            ]);
                                        } else if (category === 'REVENUE_NOT_ALLOCATED') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 100,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                },
                                                {
                                                    title: 'Est. Vendor Cost',
                                                    dataIndex: 'estimatedVendorCost',
                                                    key: 'estimatedVendorCost',
                                                    width: 160,
                                                    sorter: (a, b) => (a.estimatedVendorCost || 0) - (b.estimatedVendorCost || 0),
                                                    render: (val, record) => formatIssueCurrency(val, record),
                                                },
                                                {
                                                    title: 'Vendor Planned',
                                                    dataIndex: 'vendorPlanned',
                                                    key: 'vendorPlanned',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorPlanned || 0) - (b.vendorPlanned || 0),
                                                    render: (val, record) => {
                                                        // Show planning status - may be 0 or >0
                                                        const color = val > 0 ? '#52c41a' : '#ff9800';
                                                        return (
                                                            <span style={{ color, fontWeight: val > 0 ? 'normal' : 'bold' }}>
                                                                {formatIssueCurrency(val, record)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Revenue',
                                                    dataIndex: 'vendorRevenueUsd',
                                                    key: 'vendorRevenueUsd',
                                                    width: 150,
                                                    sorter: (a, b) => (a.vendorRevenueUsd || 0) - (b.vendorRevenueUsd || 0),
                                                    render: (val, record) => {
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                                                {formatIssueCurrency(0, record)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Revenue Gap',
                                                    dataIndex: 'revenueGap',
                                                    key: 'revenueGap',
                                                    width: 140,
                                                    sorter: (a, b) => (a.revenueGap || 0) - (b.revenueGap || 0),
                                                    render: (val, record) => (
                                                        <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                            -{formatIssueCurrency(val, record)}
                                                        </span>
                                                    ),
                                                },
                                                issuePicColumn(120),
                                            ]);
                                        } else if (category === 'PROJECTS_TO_ARCHIVE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 110,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status) => (
                                                        <span style={{ color: '#52c41a', fontWeight: '500' }}>
                                                            {status || 'Finished'}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 120,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date) => date ? new Date(date).toLocaleDateString() : '-',
                                                },
                                                {
                                                    title: 'Days Since Finished',
                                                    dataIndex: 'days_since_finished',
                                                    key: 'days_since_finished',
                                                    width: 170,
                                                    sorter: (a, b) => a.days_since_finished - b.days_since_finished,
                                                    defaultSortOrder: 'descend',
                                                    render: (days) => {
                                                        // Color coding based on age
                                                        let color = '#52c41a'; // Green: < 30 days
                                                        if (days >= 90) color = '#ff4d4f'; // Red: 90+ days
                                                        else if (days >= 60) color = '#ff9800'; // Orange: 60-89 days
                                                        else if (days >= 30) color = '#faad14'; // Yellow: 30-59 days

                                                        return (
                                                            <span style={{ color, fontWeight: 'bold', fontSize: '13px' }}>
                                                                {days} {days === 1 ? 'day' : 'days'}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                issuePicColumn(130),
                                            ]);
                                        } else if (category === 'PROJECTS_PAST_END_DATE') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 150,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status, record) => (
                                                        <EditableCell
                                                            type="select"
                                                            value={status}
                                                            options={[
                                                                { value: 'In-Planning', label: 'In-Planning' },
                                                                { value: 'On-Going', label: 'On-Going' },
                                                                { value: 'Paused', label: 'Paused' },
                                                                { value: 'Finished', label: 'Finished' },
                                                                { value: 'Archived', label: 'Archived' },
                                                                { value: 'Canceled', label: 'Canceled' },
                                                            ]}
                                                            onSave={async (newStatus) => {
                                                                await handleInlineProjectUpdate(record.project_id, { status: newStatus });
                                                            }}
                                                        />
                                                    ),
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 150,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (date, record) => (
                                                        <EditableCell
                                                            type="date"
                                                            value={date}
                                                            onSave={async (newDate) => {
                                                                await handleInlineProjectUpdate(record.project_id, { endDate: newDate });
                                                            }}
                                                        />
                                                    ),
                                                },
                                                {
                                                    title: 'Days Overdue',
                                                    dataIndex: 'daysOverdue',
                                                    key: 'daysOverdue',
                                                    width: 150,
                                                    sorter: (a, b) => (a.daysOverdue || 0) - (b.daysOverdue || 0),
                                                    defaultSortOrder: 'descend',
                                                    render: (days) => {
                                                        // Color coding: Moderate approach
                                                        // Green: 1-7 days, Yellow: 8-30 days, Orange: 31-60 days, Red: 61+ days
                                                        let color = '#52c41a'; // Green: 1-7 days
                                                        if (days >= 61) color = '#ff4d4f'; // Red: 61+ days
                                                        else if (days >= 31) color = '#ff9800'; // Orange: 31-60 days
                                                        else if (days >= 8) color = '#faad14'; // Yellow: 8-30 days

                                                        return (
                                                            <span style={{ color, fontWeight: 'bold', fontSize: '13px' }}>
                                                                {days} {days === 1 ? 'day' : 'days'}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                issuePicColumn(130),
                                            ]);
                                        } else if (category === 'VENDOR_LOSS_FINISHED') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 110,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status) => {
                                                        const color = status === 'Completed' ? '#52c41a' : '#8c8c8c';
                                                        return (
                                                            <span style={{ color, fontWeight: '500' }}>
                                                                {status}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Planned',
                                                    dataIndex: 'vendorPlannedProjectCurrency',
                                                    key: 'vendorPlannedProjectCurrency',
                                                    width: 140,
                                                    sorter: (a, b) => (a.vendorPlannedProjectCurrency || 0) - (b.vendorPlannedProjectCurrency || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return formatIssueCurrency(amount, record);
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Bills',
                                                    dataIndex: 'vendorInvoicedProjectCurrency',
                                                    key: 'vendorInvoicedProjectCurrency',
                                                    width: 140,
                                                    sorter: (a, b) => (a.vendorInvoicedProjectCurrency || 0) - (b.vendorInvoicedProjectCurrency || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return (
                                                            <span style={{ fontWeight: '500' }}>
                                                                {formatIssueCurrency(amount, record)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Revenue (SGD)',
                                                    dataIndex: 'vendorRevenueSgd',
                                                    key: 'vendorRevenueSgd',
                                                    width: 140,
                                                    sorter: (a, b) => (a.vendorRevenueSgd || 0) - (b.vendorRevenueSgd || 0),
                                                    render: (amount) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return formatSgdCurrency(amount);
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Loss',
                                                    dataIndex: 'vendorLoss',
                                                    key: 'vendorLoss',
                                                    width: 140,
                                                    sorter: (a, b) => a.vendorLoss - b.vendorLoss,
                                                    defaultSortOrder: 'descend',
                                                    render: (loss, record) => {
                                                        if (!loss && loss !== 0) return '-';
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '13px' }}>
                                                                -{formatIssueCurrency(loss, record)}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Loss %',
                                                    dataIndex: 'lossPercentage',
                                                    key: 'lossPercentage',
                                                    width: 100,
                                                    sorter: (a, b) => (a.lossPercentage || 0) - (b.lossPercentage || 0),
                                                    render: (percentage) => {
                                                        if (!percentage && percentage !== 0) return '-';
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '12px' }}>
                                                                {percentage}%
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'End/Archive Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 130,
                                                    sorter: (a, b) => {
                                                        const dateA = a.archived_at || a.end_date;
                                                        const dateB = b.archived_at || b.end_date;
                                                        const dateObjA = dateA ? new Date(dateA) : new Date(0);
                                                        const dateObjB = dateB ? new Date(dateB) : new Date(0);
                                                        return dateObjA - dateObjB;
                                                    },
                                                    render: (endDate, record) => {
                                                        // Show archived_at if available, otherwise end_date
                                                        const dateToShow = record.archived_at || endDate;
                                                        if (!dateToShow) return '-';
                                                        const formattedDate = new Date(dateToShow).toLocaleDateString();
                                                        const source = record.archived_at ? 'Archived' : 'Ended';
                                                        return (
                                                            <span title={`${source} on ${formattedDate}`}>
                                                                {formattedDate}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                issuePicColumn(130),
                                            ]);
                                        } else if (category === 'UNPROFITABLE_FINISHED') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 110,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status) => {
                                                        const color = status === 'Finished' ? '#52c41a' : '#8c8c8c';
                                                        return (
                                                            <span style={{ color, fontWeight: '500' }}>
                                                                {status}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Project Billings',
                                                    dataIndex: 'invoicedRevenue',
                                                    key: 'invoicedRevenue',
                                                    width: 150,
                                                    sorter: (a, b) => (a.invoicedRevenue || 0) - (b.invoicedRevenue || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return `${record.currencySymbol}${amount.toLocaleString()}`;
                                                    },
                                                },
                                                {
                                                    title: 'Manhour Cost',
                                                    dataIndex: 'manhourCostProjectCurrency',
                                                    key: 'manhourCostProjectCurrency',
                                                    width: 140,
                                                    sorter: (a, b) => (a.manhourCostProjectCurrency || 0) - (b.manhourCostProjectCurrency || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return (
                                                            <span style={{ fontWeight: '500' }}>
                                                                {record.currencySymbol}{amount.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Bills',
                                                    dataIndex: 'vendorBills',
                                                    key: 'vendorBills',
                                                    width: 140,
                                                    sorter: (a, b) => (a.vendorBills || 0) - (b.vendorBills || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return (
                                                            <span style={{ fontWeight: '500' }}>
                                                                {record.currencySymbol}{amount.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Total Loss',
                                                    dataIndex: 'totalLoss',
                                                    key: 'totalLoss',
                                                    width: 140,
                                                    sorter: (a, b) => a.totalLoss - b.totalLoss,
                                                    defaultSortOrder: 'descend',
                                                    render: (loss, record) => {
                                                        if (!loss && loss !== 0) return '-';
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '13px' }}>
                                                                -{record.currencySymbol}{loss.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Loss %',
                                                    dataIndex: 'lossPercentage',
                                                    key: 'lossPercentage',
                                                    width: 100,
                                                    sorter: (a, b) => (a.lossPercentage || 0) - (b.lossPercentage || 0),
                                                    render: (percentage) => {
                                                        if (!percentage && percentage !== 0) return '-';
                                                        return (
                                                            <span style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '12px' }}>
                                                                {percentage}%
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'End Date',
                                                    dataIndex: 'end_date',
                                                    key: 'end_date',
                                                    width: 130,
                                                    sorter: (a, b) => {
                                                        const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                                                        const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                                                        return dateA - dateB;
                                                    },
                                                    render: (endDate) => {
                                                        if (!endDate) return '-';
                                                        return new Date(endDate).toLocaleDateString();
                                                    },
                                                },
                                                issuePicColumn(130),
                                            ]);
                                        } else if (category === 'HIGHLY_PROFITABLE_ARCHIVED') {
                                            return appendIssueRemarkColumn([
                                                ...baseColumns,
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'status',
                                                    key: 'status',
                                                    width: 110,
                                                    sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
                                                    render: (status) => {
                                                        return (
                                                            <span style={{ color: '#8c8c8c', fontWeight: '500' }}>
                                                                {status}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Project Billings',
                                                    dataIndex: 'invoicedRevenue',
                                                    key: 'invoicedRevenue',
                                                    width: 150,
                                                    sorter: (a, b) => (a.invoicedRevenue || 0) - (b.invoicedRevenue || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        const hasWarning = record.isZeroBilling;
                                                        return (
                                                            <span title={hasWarning ? "Zero billing - unusual case" : ""}>
                                                                {record.currencySymbol}{amount.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Manhour Cost',
                                                    dataIndex: 'manhourCostProjectCurrency',
                                                    key: 'manhourCostProjectCurrency',
                                                    width: 140,
                                                    sorter: (a, b) => (a.manhourCostProjectCurrency || 0) - (b.manhourCostProjectCurrency || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return (
                                                            <span style={{ fontWeight: '500' }}>
                                                                {record.currencySymbol}{amount.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Vendor Bills',
                                                    dataIndex: 'vendorBills',
                                                    key: 'vendorBills',
                                                    width: 140,
                                                    sorter: (a, b) => (a.vendorBills || 0) - (b.vendorBills || 0),
                                                    render: (amount, record) => {
                                                        if (!amount && amount !== 0) return '-';
                                                        return (
                                                            <span style={{ fontWeight: '500' }}>
                                                                {record.currencySymbol}{amount.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Total Profit',
                                                    dataIndex: 'totalProfit',
                                                    key: 'totalProfit',
                                                    width: 140,
                                                    sorter: (a, b) => a.totalProfit - b.totalProfit,
                                                    defaultSortOrder: 'descend',
                                                    render: (profit, record) => {
                                                        if (!profit && profit !== 0) return '-';
                                                        return (
                                                            <span style={{ color: '#52c41a', fontWeight: 'bold', fontSize: '13px' }}>
                                                                +{record.currencySymbol}{profit.toLocaleString()}
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Profit Margin',
                                                    dataIndex: 'profitMargin',
                                                    key: 'profitMargin',
                                                    width: 120,
                                                    sorter: (a, b) => parseFloat(a.profitMargin) - parseFloat(b.profitMargin),
                                                    render: (margin) => {
                                                        if (!margin && margin !== 0) return '-';
                                                        return (
                                                            <span style={{
                                                                color: '#52c41a',
                                                                fontWeight: 'bold',
                                                                fontSize: '12px',
                                                                padding: '2px 8px',
                                                                background: '#f6ffed',
                                                                borderRadius: '4px',
                                                                border: '1px solid #b7eb8f'
                                                            }}>
                                                                {margin}%
                                                            </span>
                                                        );
                                                    },
                                                },
                                                {
                                                    title: 'Archive Date',
                                                    dataIndex: 'archived_at',
                                                    key: 'archived_at',
                                                    width: 130,
                                                    sorter: (a, b) => {
                                                        const dateA = a.archived_at || a.end_date;
                                                        const dateB = b.archived_at || b.end_date;
                                                        const dateObjA = dateA ? new Date(dateA) : new Date(0);
                                                        const dateObjB = dateB ? new Date(dateB) : new Date(0);
                                                        return dateObjA - dateObjB;
                                                    },
                                                    render: (archivedAt, record) => {
                                                        const dateToShow = archivedAt || record.end_date;
                                                        if (!dateToShow) return '-';
                                                        return new Date(dateToShow).toLocaleDateString();
                                                    },
                                                },
                                                issuePicColumn(130),
                                            ]);
                                        }

                                        return baseColumns;
                                    };

                                    // Check if this is Projects page (not My Projects) and has owner grouping
                                    const hasOwnerGrouping = group.groupedByOwner && group.groupedByOwner.length > 0;
                                    const isProjectsPage = !myProjects && !isMyProjects;

                                    return {
                                        key: group.category,
                                        style: { marginBottom: 0 },
                                        label: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <Badge key="badge" count={group.count} style={{ backgroundColor: color }} />
                                                <span key="icon" style={{ color, fontSize: 16 }}>{icon}</span>
                                                <Typography.Text key="title" strong>{ISSUE_CATEGORIES[group.category]?.label || group.title || 'Unknown Issue'}</Typography.Text>
                                                <Popover
                                                    key="help"
                                                    content={getIssueDocumentationPopover(group.category)}
                                                    title="Issue Documentation"
                                                    trigger="click"
                                                    placement="right"
                                                    overlayStyle={{ maxWidth: 500 }}
                                                >
                                                    <QuestionCircleOutlined
                                                        style={{
                                                            fontSize: 12,
                                                            color: "#8c8c8c",
                                                            cursor: "pointer",
                                                            marginLeft: 4
                                                        }}
                                                    />
                                                </Popover>
                                            </div>
                                        ),
                                        children: (
                                            <>
                                                {/* Nested collapse by owner for Projects page */}
                                                {isProjectsPage && hasOwnerGrouping ? (
                                                <Collapse
                                                    style={{
                                                        marginLeft: 0,
                                                        border: 'none',
                                                        background: 'transparent'
                                                    }}
                                                    defaultActiveKey={group.groupedByOwner.slice(0, 3).map(owner => owner.owner)}
                                                    items={group.groupedByOwner.map((ownerGroup) => ({
                                                        key: ownerGroup.owner,
                                                        style: {
                                                            marginBottom: 8,
                                                            border: '1px solid #d9d9d9',
                                                            borderRadius: 4,
                                                            background: '#fff'
                                                        },
                                                        label: (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                                    <Badge key="badge" count={ownerGroup.count} style={{ backgroundColor: '#1890ff' }} />
                                                                    <Typography.Text key="owner" strong>
                                                                        {ownerGroup.owner}
                                                                    </Typography.Text>
                                                                    {ownerGroup.totals && Object.keys(ownerGroup.totals).length > 0 && (
                                                                        <div key="totals" style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: 12 }}>
                                                                            {ownerGroup.totals.totalVendorLoss && (
                                                                                <Typography.Text key="vendor-loss" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                                                                                    Loss: {formatTotalCurrency(ownerGroup.totals.totalVendorLoss, ownerGroup.totals)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalShortfall && (
                                                                                <Typography.Text key="shortfall" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                                                                                    Shortfall: {formatSgdCurrency(ownerGroup.totals.totalShortfall ?? ownerGroup.totals.totalShortfallSgd)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalSurplus && (
                                                                                <Typography.Text key="surplus" style={{ color: '#52c41a', fontWeight: 600 }}>
                                                                                    Surplus: {formatSgdCurrency(ownerGroup.totals.totalSurplus ?? ownerGroup.totals.totalSurplusSgd)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalDiscrepancy && (
                                                                                <Typography.Text key="discrepancy" style={{ color: '#faad14', fontWeight: 600 }}>
                                                                                    Difference: {formatSgdCurrency(ownerGroup.totals.totalDiscrepancy ?? ownerGroup.totals.totalDiscrepancySgd)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalOverBudget && (
                                                                                <Typography.Text key="over-budget" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                                                                                    Over Budget: {formatSgdCurrency(ownerGroup.totals.totalOverBudget ?? ownerGroup.totals.totalOverBudgetSgd)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalLoss && (
                                                                                <Typography.Text key="total-loss" style={{ color: '#ff4d4f', fontWeight: 600 }}>
                                                                                    Loss: {formatSgdCurrency(ownerGroup.totals.totalLoss ?? ownerGroup.totals.totalLossSgd)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalPlanningGap && (
                                                                                <Typography.Text key="planning-gap" style={{ color: '#faad14', fontWeight: 600 }}>
                                                                                    Gap: {formatTotalCurrency(ownerGroup.totals.totalPlanningGap, ownerGroup.totals)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalRevenueGap && (
                                                                                <Typography.Text key="revenue-gap" style={{ color: '#faad14', fontWeight: 600 }}>
                                                                                    Gap: {formatTotalCurrency(ownerGroup.totals.totalRevenueGap, ownerGroup.totals)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalVariance && (
                                                                                <Typography.Text key="variance" style={{ color: '#faad14', fontWeight: 600 }}>
                                                                                    Variance: {formatTotalCurrency(ownerGroup.totals.totalVariance, ownerGroup.totals)}
                                                                                </Typography.Text>
                                                                            )}
                                                                            {ownerGroup.totals.totalProfit && (
                                                                                <Typography.Text key="profit" style={{ color: '#52c41a', fontWeight: 600 }}>
                                                                                    Profit: {formatTotalCurrency(ownerGroup.totals.totalProfit, ownerGroup.totals)}
                                                                                </Typography.Text>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                        ),
                                                        children: (
                                                            <Table
                                                                columns={getColumns(group.category)}
                                                                dataSource={ownerGroup.projects}
                                                                rowKey="project_id"
                                                                pagination={false}
                                                                size="small"
                                                                summary={() => {
                                                                    if (!ownerGroup.totals || Object.keys(ownerGroup.totals).length === 0) {
                                                                        return null;
                                                                    }

                                                                    const renderRemarkSummaryCell = (key) => (
                                                                        ISSUE_REMARK_CATEGORIES.has(group.category)
                                                                            ? <Table.Summary.Cell key={key} />
                                                                            : null
                                                                    );

                                                                    // Render totals aligned with columns (NO index prop - auto-aligns)
                                                                    const renderOwnerTotalCells = () => {
                                                                        switch(group.category) {
                                                                            case 'VENDOR_BILLS_OVER_REVENUE':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorBills ?? ownerGroup.totals.totalVendorBillsSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorRevenue ?? ownerGroup.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(ownerGroup.totals.totalLoss ?? ownerGroup.totals.totalLossSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_REVENUE_LOW':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorRevenue ?? ownerGroup.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="shortfall"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(ownerGroup.totals.totalShortfall ?? ownerGroup.totals.totalShortfallSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_REVENUE_HIGH':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorRevenue ?? ownerGroup.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="surplus"><Typography.Text strong style={{ color: '#52c41a' }}>+{formatSgdCurrency(ownerGroup.totals.totalSurplus ?? ownerGroup.totals.totalSurplusSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_REVENUE_PLANNED_COST_DISCREPANCY':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorRevenue ?? ownerGroup.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="discrepancy"><Typography.Text strong style={{ color: '#faad14' }}>{formatSgdCurrency(ownerGroup.totals.totalDiscrepancy ?? ownerGroup.totals.totalDiscrepancySgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        <Table.Summary.Cell key="empty3" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_BILLS_OVER_PLANNED':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorBills ?? ownerGroup.totals.totalVendorBillsSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="over-budget"><Typography.Text strong style={{ color: '#ff4d4f' }}>+{formatSgdCurrency(ownerGroup.totals.totalOverBudget ?? ownerGroup.totals.totalOverBudgetSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_LOSS_FINISHED':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-invoiced"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalVendorInvoiced, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorRevenue ?? ownerGroup.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatTotalCurrency(ownerGroup.totals.totalVendorLoss, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        <Table.Summary.Cell key="empty3" />
                                                                                        <Table.Summary.Cell key="empty4" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'UNPROFITABLE_FINISHED':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="invoiced-revenue"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalInvoicedRevenue, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="manhour-cost"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalManhourCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalVendorBillsUsd, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(ownerGroup.totals.totalLoss ?? ownerGroup.totals.totalLossSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        <Table.Summary.Cell key="empty3" />
                                                                                        <Table.Summary.Cell key="empty4" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'HIGHLY_PROFITABLE_ARCHIVED':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="invoiced-revenue"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalInvoicedRevenue, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="manhour-cost"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalManhourCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalVendorBillsUsd, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="profit"><Typography.Text strong style={{ color: '#52c41a' }}>+{formatTotalCurrency(ownerGroup.totals.totalProfit, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        <Table.Summary.Cell key="empty2" />
                                                                                        <Table.Summary.Cell key="empty3" />
                                                                                        <Table.Summary.Cell key="empty4" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'VENDOR_PLANNING_EMPTY':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="estimated-cost">
                                                                                            <Typography.Text strong style={{ color: ownerGroup.totals.error ? '#ff4d4f' : 'inherit' }}>
                                                                                                {ownerGroup.totals.error ? 'ERROR: Missing USD conversion' : formatTotalCurrency(ownerGroup.totals.totalEstimatedVendorCost, group.totals)}
                                                                                            </Typography.Text>
                                                                                        </Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatTotalCurrency(0, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="planning-gap">
                                                                                            <Typography.Text strong style={{ color: ownerGroup.totals.error ? '#ff4d4f' : '#ff9800' }}>
                                                                                                {ownerGroup.totals.error ? 'ERROR' : `-${formatTotalCurrency(ownerGroup.totals.totalPlanningGap, group.totals)}`}
                                                                                            </Typography.Text>
                                                                                        </Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            case 'REVENUE_NOT_ALLOCATED':
                                                                                return (
                                                                                    <>
                                                                                        <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="estimated-cost"><Typography.Text strong>{formatTotalCurrency(ownerGroup.totals.totalEstimatedVendorCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(ownerGroup.totals.totalVendorPlanned ?? ownerGroup.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatTotalCurrency(0, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="revenue-gap"><Typography.Text strong style={{ color: '#faad14' }}>-{formatTotalCurrency(ownerGroup.totals.totalRevenueGap, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                                        <Table.Summary.Cell key="empty1" />
                                                                                        {renderRemarkSummaryCell('remark')}
                                                                                    </>
                                                                                );
                                                                            default:
                                                                                return <Table.Summary.Cell key="label" colSpan={getColumns(group.category).length}><Typography.Text strong>TOTAL ({ownerGroup.owner})</Typography.Text></Table.Summary.Cell>;
                                                                        }
                                                                    };

                                                                    return (
                                                                        <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                                                                            {renderOwnerTotalCells()}
                                                                        </Table.Summary.Row>
                                                                    );
                                                                }}
                                                            />
                                                        )
                                                    }))}
                                                />
                                            ) : (
                                                /* My Projects page - flat list */
                                                <Table
                                                    columns={getColumns(group.category)}
                                                    dataSource={group.projects}
                                                    rowKey="project_id"
                                                    pagination={false}
                                                    size="small"
                                                    summary={() => {
                                                        if (!group.totals || Object.keys(group.totals).length === 0) {
                                                            return null;
                                                        }

                                                        const renderRemarkSummaryCell = (key) => (
                                                            ISSUE_REMARK_CATEGORIES.has(group.category)
                                                                ? <Table.Summary.Cell key={key} />
                                                                : null
                                                        );

                                                        // Render totals aligned with columns (NO index prop - auto-aligns)
                                                        const renderTotalCells = () => {
                                                            switch(group.category) {
                                                                case 'VENDOR_BILLS_OVER_REVENUE':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorBills ?? group.totals.totalVendorBillsSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorRevenue ?? group.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(group.totals.totalLoss ?? group.totals.totalLossSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_REVENUE_LOW':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorRevenue ?? group.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorPlanned ?? group.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="shortfall"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatSgdCurrency(group.totals.totalShortfall ?? group.totals.totalShortfallSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_REVENUE_HIGH':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorRevenue ?? group.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorPlanned ?? group.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="surplus"><Typography.Text strong style={{ color: '#52c41a' }}>+{formatSgdCurrency(group.totals.totalSurplus ?? group.totals.totalSurplusSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_REVENUE_PLANNED_COST_DISCREPANCY':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorRevenue ?? group.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorPlanned ?? group.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="discrepancy"><Typography.Text strong style={{ color: '#faad14' }}>{formatSgdCurrency(group.totals.totalDiscrepancy ?? group.totals.totalDiscrepancySgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            <Table.Summary.Cell key="empty3" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_BILLS_OVER_PLANNED':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={2}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorBills ?? group.totals.totalVendorBillsSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorPlanned ?? group.totals.totalVendorPlannedSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="over-budget"><Typography.Text strong style={{ color: '#ff4d4f' }}>+{formatSgdCurrency(group.totals.totalOverBudget ?? group.totals.totalOverBudgetSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_LOSS_FINISHED':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatTotalCurrency(group.totals.totalVendorPlanned, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-invoiced"><Typography.Text strong>{formatTotalCurrency(group.totals.totalVendorInvoiced, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatSgdCurrency(group.totals.totalVendorRevenue ?? group.totals.totalVendorRevenueSgd)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatTotalCurrency(group.totals.totalVendorLoss, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            <Table.Summary.Cell key="empty3" />
                                                                            <Table.Summary.Cell key="empty4" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'UNPROFITABLE_FINISHED':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="invoiced-revenue"><Typography.Text strong>{formatTotalCurrency(group.totals.totalInvoicedRevenue, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="manhour-cost"><Typography.Text strong>{formatTotalCurrency(group.totals.totalManhourCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatTotalCurrency(group.totals.totalVendorBillsUsd, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="loss"><Typography.Text strong style={{ color: '#ff4d4f' }}>-{formatTotalCurrency(group.totals.totalLoss, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            <Table.Summary.Cell key="empty3" />
                                                                            <Table.Summary.Cell key="empty4" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'HIGHLY_PROFITABLE_ARCHIVED':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="invoiced-revenue"><Typography.Text strong>{formatTotalCurrency(group.totals.totalInvoicedRevenue, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="manhour-cost"><Typography.Text strong>{formatTotalCurrency(group.totals.totalManhourCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-bills"><Typography.Text strong>{formatTotalCurrency(group.totals.totalVendorBillsUsd, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="profit"><Typography.Text strong style={{ color: '#52c41a' }}>+{formatTotalCurrency(group.totals.totalProfit, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            <Table.Summary.Cell key="empty2" />
                                                                            <Table.Summary.Cell key="empty3" />
                                                                            <Table.Summary.Cell key="empty4" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'VENDOR_PLANNING_EMPTY':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="estimated-cost">
                                                                                <Typography.Text strong style={{ color: group.totals.error ? '#ff4d4f' : 'inherit' }}>
                                                                                    {group.totals.error ? 'ERROR: Missing USD conversion' : formatTotalCurrency(group.totals.totalEstimatedVendorCost, group.totals)}
                                                                                </Typography.Text>
                                                                            </Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatTotalCurrency(0, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="planning-gap">
                                                                                <Typography.Text strong style={{ color: group.totals.error ? '#ff4d4f' : '#ff9800' }}>
                                                                                    {group.totals.error ? 'ERROR' : `-${formatTotalCurrency(group.totals.totalPlanningGap, group.totals)}`}
                                                                                </Typography.Text>
                                                                            </Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                case 'REVENUE_NOT_ALLOCATED':
                                                                    return (
                                                                        <>
                                                                            <Table.Summary.Cell key="label" colSpan={3}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="estimated-cost"><Typography.Text strong>{formatTotalCurrency(group.totals.totalEstimatedVendorCost, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-planned"><Typography.Text strong>{formatTotalCurrency(group.totals.totalVendorPlanned, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="vendor-revenue"><Typography.Text strong>{formatTotalCurrency(0, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="revenue-gap"><Typography.Text strong style={{ color: '#faad14' }}>-{formatTotalCurrency(group.totals.totalRevenueGap, group.totals)}</Typography.Text></Table.Summary.Cell>
                                                                            <Table.Summary.Cell key="empty1" />
                                                                            {renderRemarkSummaryCell('remark')}
                                                                        </>
                                                                    );
                                                                default:
                                                                    return <Table.Summary.Cell key="label" colSpan={getColumns(group.category).length}><Typography.Text strong>TOTAL</Typography.Text></Table.Summary.Cell>;
                                                            }
                                                        };

                                                        return (
                                                            <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                                                                {renderTotalCells()}
                                                            </Table.Summary.Row>
                                                        );
                                                    }}
                                                />
                                            )}
                                            </>
                                        )
                                    };
                                })}
                            />
                        </>
                            )}
                        </>
                    )}
                </div>
            ) : (
                // Regular Table View
                <Table
                    columns={columns}
                    dataSource={filteredProjects}
                    rowKey={getProjectTableRowKey}
                    loading={loading}
                    onChange={handleTableChange}
                    expandable={showCreditExposure ? {
                        expandedRowRender: renderCreditExposureBreakdown,
                        rowExpandable: (record) => Boolean(record.creditExposure),
                        expandedRowKeys: expandedCreditRowKeys,
                        onExpandedRowsChange: setExpandedCreditRowKeys,
                    } : undefined}
                pagination={
                    pagination
                        ? {
                              current: pagination.page,
                              pageSize: pagination.limit,
                              total: pagination.total,
                              showSizeChanger: false,
                              showTotal: (total, range) =>
                                  `${range[0]}-${range[1]} of ${total} projects`,
                              onChange: (page, _pageSize) => {
                                  if (externalOnPageChange) {
                                      // External control mode
                                      externalOnPageChange(page);
                                  } else {
                                      // Self-managed mode
                                      fetchProjectsRequestSeqRef.current += 1;
                                      setInternalPagination(prev => ({
                                          ...prev,
                                          page: page
                                      }));
                                  }
                              },
                          }
                        : {
                              pageSize: 10,
                              showSizeChanger: false,
                              showTotal: (total, range) =>
                                  `${range[0]}-${range[1]} of ${total} projects`,
                          }
                }
                className="projects-table data-table"
                />
            )}

            <ExportProgressModal
                visible={Boolean(exportJob)}
                jobId={exportJob?.jobId}
                statusEndpoint={exportJob?.endpoint}
                cancelEndpoint={exportJob?.endpoint}
                title="Projects Export"
                onComplete={handleExportComplete}
                onCancel={() => setExportJob(null)}
            />

            {/* Export Modal */}
            <Modal
                title="Export"
                open={exportModalVisible}
                onCancel={handleExportCancel}
                width={400}
                wrapClassName="pm-modal"
                centered={true}
                mask={{ closable: !exportLoading }}
                closable={!exportLoading}
                styles={{
                    body: {
                        padding: "16px 0px",
                        minHeight: "auto",
                    },
                    header: {
                        padding: "0px 0px",
                        marginBottom: 0,
                    },
                    footer: {
                        margin: "0px 0px",
                    }
                }}
                footer={exportLoading ? null : [
                    <Button
                        key="local"
                        onClick={() => handleExportConfirm("local")}
                        disabled={exportLoading}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        LOCAL
                    </Button>,
                    <Button
                        key="google"
                        onClick={() => handleExportConfirm("google")}
                        disabled={exportLoading}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        GOOGLE
                    </Button>,
                ]}
            >
                <div
                    style={{
                        textAlign: "center",
                    }}
                >
                    {exportLoading ? (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, minHeight: '24px' }}>
                                <Spin size="small" style={{ marginRight: 12, display: 'flex', alignItems: 'center' }} />
                                <Progress
                                    percent={exportProgress}
                                    strokeColor="#FED230"
                                    style={{ flex: 1, margin: 0 }}
                                />
                            </div>
                            <p style={{ fontSize: 15, color: "#595959", margin: 0 }}>
                                Exporting projects, please wait...
                            </p>
                        </div>
                    ) : exportError ? (
                        <div>
                            <p style={{ fontSize: 15, color: "#ff4d4f", marginBottom: 16 }}>
                                ❌ {exportError}
                            </p>
                            <p style={{ fontSize: 13, color: "#999", margin: 0 }}>
                                Please try again.
                            </p>
                        </div>
                    ) : (
                        <p
                            style={{
                                fontSize: 15,
                                color: "#595959",
                                margin: 0,
                            }}
                        >
                            Where do you want the file to be saved?
                        </p>
                    )}
                </div>
            </Modal>

            {/* Monthly Export Modal */}
            <Modal
                title="Export Monthly Report"
                open={monthlyExportModalVisible}
                onCancel={handleMonthlyExportCancel}
                width={450}
                wrapClassName="pm-modal"
                centered={false}
                mask={{ closable: !monthlyExportLoading }}
                closable={!monthlyExportLoading}
                styles={{
                    body: {
                        padding: "16px 24px",
                        minHeight: "auto",
                    },
                    header: {
                        padding: "16px 24px 10px",
                        marginBottom: 0,
                    },
                }}
                footer={monthlyExportLoading ? null : [
                    <Button
                        key="cancel"
                        onClick={handleMonthlyExportCancel}
                        disabled={monthlyExportLoading}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        CANCEL
                    </Button>,
                    <Button
                        key="local"
                        onClick={() => handleMonthlyExport("local")}
                        disabled={monthlyExportLoading || !selectedMonth || !selectedYear}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        LOCAL
                    </Button>,
                    <Button
                        key="google"
                        onClick={() => handleMonthlyExport("google")}
                        disabled={monthlyExportLoading || !selectedMonth || !selectedYear}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        GOOGLE
                    </Button>,
                ]}
            >
                <div style={{ padding: "10px 0" }}>
                    {monthlyExportLoading ? (
                        <div style={{ textAlign: "center" }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, minHeight: '24px' }}>
                                <Spin size="small" style={{ marginRight: 12, display: 'flex', alignItems: 'center' }} />
                                <Progress
                                    percent={monthlyExportProgress}
                                    strokeColor="#FED230"
                                    style={{ flex: 1, margin: 0 }}
                                />
                            </div>
                            <p style={{ fontSize: 15, color: "#595959", margin: 0 }}>
                                Generating monthly report, please wait...
                            </p>
                        </div>
                    ) : monthlyExportError ? (
                        <div style={{ textAlign: "center" }}>
                            <p style={{ fontSize: 15, color: "#ff4d4f", marginBottom: 16 }}>
                                ❌ {monthlyExportError}
                            </p>
                            <p style={{ fontSize: 13, color: "#999", margin: 0 }}>
                                Please try again.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ display: "block", marginBottom: 8, fontWeight: "500" }}>
                                    Month *
                                </label>
                                <Select
                                    placeholder="Select Month"
                                    style={{ width: "100%" }}
                                    value={selectedMonth}
                                    onChange={setSelectedMonth}
                                    disabled={monthlyExportLoading}
                                >
                                    <Option value="1">January</Option>
                                    <Option value="2">February</Option>
                                    <Option value="3">March</Option>
                                    <Option value="4">April</Option>
                                    <Option value="5">May</Option>
                                    <Option value="6">June</Option>
                                    <Option value="7">July</Option>
                                    <Option value="8">August</Option>
                                    <Option value="9">September</Option>
                                    <Option value="10">October</Option>
                                    <Option value="11">November</Option>
                                    <Option value="12">December</Option>
                                </Select>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ display: "block", marginBottom: 8, fontWeight: "500" }}>
                                    Year *
                                </label>
                                <Select
                                    placeholder="Select Year"
                                    style={{ width: "100%" }}
                                    value={selectedYear}
                                    onChange={setSelectedYear}
                                    disabled={monthlyExportLoading}
                                >
                                    <Option value={2022}>2022</Option>
                                    <Option value={2023}>2023</Option>
                                    <Option value={2024}>2024</Option>
                                    <Option value={2025}>2025</Option>
                                    <Option value={2026}>2026</Option>
                                </Select>
                            </div>
                            <p style={{ fontSize: 13, color: "#999", fontStyle: "italic", margin: 0 }}>
                                * Monthly report includes all project types with monthly breakdown data
                            </p>
                        </>
                    )}
                </div>
            </Modal>

            {/* Duplicate Project Modal */}
            <Modal
                title="Duplicate Project?"
                open={duplicateModalVisible}
                onCancel={handleDuplicateCancel}
                footer={[
                    <Button
                        key="cancel"
                        onClick={handleDuplicateCancel}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#fff",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        CANCEL
                    </Button>,
                    <Button
                        key="ok"
                        type="primary"
                        loading={duplicateLoading}
                        onClick={handleDuplicateConfirm}
                        style={{
                            minWidth: 100,
                            height: 40,
                            borderRadius: 4,
                            backgroundColor: "#FED230",
                            border: "none",
                            color: "#1b1b1b",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        OK
                    </Button>,
                ]}
            >
                <div
                    style={{
                        padding: "10px 0",
                        fontSize: 15,
                        color: "#595959",
                    }}
                >
                    <p style={{ marginBottom: 10 }}>
                        {projectToDuplicate
                            ? `MC x ${projectToDuplicate.name} Mgmt_${
                                  new Date().toISOString().split("T")[0]
                              }`
                            : ""}
                    </p>
                    <p
                        style={{
                            fontSize: 13,
                            color: "#999",
                            fontStyle: "italic",
                        }}
                    >
                        * duplicated project only copy project information
                        detail
                    </p>
                </div>
            </Modal>

            <IssueRemarkModal
                visible={issueRemarkEditor.visible}
                editor={issueRemarkEditor}
                saving={issueRemarkSaving}
                onClose={closeIssueRemarkEditor}
                onSave={saveIssueRemark}
            />

            {/* Delete Project Confirmation Modal */}
            <DSModal
                visible={deleteModalVisible}
                onClose={handleCancelDelete}
                width="small"
            >
                <DSModal.Header
                    title="Delete Project"
                    subtitle={projectToDelete?.name}
                />
                <DSModal.Body>
                    <p style={{ marginBottom: '16px', fontSize: '14px' }}>
                        Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
                    </p>

                    {/* Error message box - only show when there's an error */}
                    {deleteError && (
                        <div style={{
                            backgroundColor: '#fff2e8',
                            border: '1px solid #ffbb96',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ fontSize: '13px', color: '#d4380d', marginBottom: '8px' }}>
                                <ExclamationCircleOutlined style={{ marginRight: '6px' }} />
                                {deleteError.split('\n').map((line, idx) => {
                                    if (line.startsWith('- ')) {
                                        return idx === 0 ? line.substring(2) : null;
                                    }
                                    return idx === 0 ? line : null;
                                })}
                            </div>
                            <ul style={{
                                fontSize: '12px',
                                color: '#8c8c8c',
                                margin: 0,
                                paddingLeft: '20px',
                                lineHeight: '1.6'
                            }}>
                                {deleteError.split('\n').map((line, idx) => {
                                    if (line.startsWith('- ')) {
                                        return <li key={idx}>{line.substring(2)}</li>;
                                    }
                                    return null;
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Requirements box - only show when no error */}
                    {!deleteError && (
                        <div style={{
                            backgroundColor: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ fontSize: '13px', color: '#d46b08', marginBottom: '8px' }}>
                                <WarningOutlined style={{ marginRight: '6px' }} />
                                Requirements for deletion:
                            </div>
                            <ul style={{
                                fontSize: '12px',
                                color: '#8c8c8c',
                                margin: 0,
                                paddingLeft: '20px',
                                lineHeight: '1.6'
                            }}>
                                <li>No linked Sales Orders (includes invoices & POs)</li>
                                <li>No Vendor Quotations (includes vendor bills)</li>
                            </ul>
                        </div>
                    )}
                </DSModal.Body>
                <DSModal.Footer
                    buttons={[
                        {
                            ...BUTTON_PRESETS.CANCEL,
                            key: 'cancel',
                            label: 'Cancel',
                            onClick: handleCancelDelete
                        },
                        {
                            ...BUTTON_PRESETS.DELETE,
                            key: 'delete',
                            label: 'Delete',
                            onClick: handleDeleteProject,
                            loading: isDeleting
                        }
                    ]}
                />
            </DSModal>

        </div>
    );
};

export default ProjectsTable;
